<template>
  <div class="step-device-access d-flex flex-column" :class="{ 'step-device-access--readonly': readOnly }">
    <div class="device-summary d-flex flex-wrap ga-4 mb-6">
      <DataCard v-for="card in deviceSummaryCards" :key="card.key"
        class="device-summary-card rounded-xl pa-4 flex-grow-1" :title="card.label" :value="card.value"
        :unit="card.unit" value-style="font-size: 24px; font-weight: 600; color: var(--color-text); line-height: 1.2;">
        <template #icon>
          <v-icon :icon="card.icon" size="28" color="primary" aria-hidden="true" />
        </template>
      </DataCard>
    </div>

    <div class="device-layout d-flex flex-column flex-md-row flex-grow-1 ga-0 min-h-0">
      <aside class="device-nav flex-shrink-0">
        <button v-for="nav in deviceNavItems" :key="nav.key" type="button"
          class="device-nav-item d-flex align-center ga-3 w-100 text-left" :class="{ active: deviceNavKey === nav.key }"
          @click="scrollDeviceSection(nav.key)">
          <component :is="nav.icon" :size="20" class="device-nav-item__icon" />
          <span class="text-body-2">{{ nav.label }}</span>
        </button>
      </aside>
      <div class="device-nav-divider flex-shrink-0 d-none d-md-block" aria-hidden="true" />
      <main ref="deviceMainScrollRef" class="device-main flex-grow-1 pa-4 pa-md-6 min-h-0">
        <section id="device-section-ess" class="device-section" aria-labelledby="device-section-ess-title">
          <div class="d-flex align-center justify-space-between flex-wrap ga-2 device-section-head">
            <h2 id="device-section-ess-title" class="section-title device-section-title mb-0">储能系统</h2>
            <v-btn v-if="!readOnly" color="var(--color-primary)" variant="tonal" size="small" rounded="lg"
              prepend-icon="mdi-plus" @click="openElinkAdd">
              新增储能方阵
            </v-btn>
          </div>
          <div class="elink-list d-flex flex-column ga-3">
            <div v-for="row in elinkRows" :key="row.id" class="elink-card rounded-xl"
              :class="{ 'is-open': isElinkExpanded(row.id) }">
              <div class="elink-card-head d-flex align-center ga-2 pa-4">
                <div class="elink-card-head-main d-flex align-center flex-wrap ga-3 flex-grow-1 min-width-0"
                  role="button" tabindex="0" @click="toggleElink(row.id)" @keydown.enter.prevent="toggleElink(row.id)"
                  @keydown.space.prevent="toggleElink(row.id)"
                  :aria-expanded="isElinkExpanded(row.id) ? 'true' : 'false'" :aria-controls="`elink-expand-${row.id}`">
                  <v-icon class="elink-chevron flex-shrink-0" size="22" color="medium-emphasis">
                    {{ isElinkExpanded(row.id) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                  </v-icon>
                  <span class="elink-badge rounded-circle d-inline-flex align-center justify-center flex-shrink-0">{{
                    row.badge ?? row.id
                  }}</span>
                  <div class="d-inline-flex flex-column flex-shrink-0 min-width-0 elink-name-stack">
                    <span class="text-body-1 font-weight-medium">{{ row.name }}</span>
                    <span v-if="!isDistributedStation && (row.galaxyUuid ?? '').trim()"
                      class="text-caption text-medium-emphasis elink-galaxy-uuid" :title="row.galaxyUuid">{{
                        row.galaxyUuid }}</span>
                  </div>
                  <v-chip v-if="!isDistributedStation && (row.galaxyModelTag ?? '').trim()" size="small" variant="flat"
                    class="elink-model-chip flex-shrink-0">{{ row.galaxyModelTag }}</v-chip>
                  <span class="d-inline-flex align-center ga-2 text-body-2 text-medium-emphasis">
                    <v-icon size="18">mdi-file-document-outline</v-icon>
                    计量电表数量: {{ meteringMeterCount(row) }}
                  </span>
                  <span class="d-inline-flex align-center ga-2 text-body-2 elink-eblock-summary">
                    <v-icon size="18" class="elink-eblock-summary-icon">mdi-cube-outline</v-icon>
                    <template v-if="isDistributedStation">
                      eBlock:
                      <span class="elink-eblock-count">{{ eblockCountForRow(row) }}</span>
                      个
                    </template>
                    <template v-else>
                      电池簇:
                      <span class="elink-eblock-count">{{ clusterCountForRow(row) }}</span>
                      个
                    </template>
                  </span>
                </div>
                <div v-if="!readOnly" class="elink-card-actions d-flex align-center ga-1 flex-shrink-0" @click.stop>
                  <v-btn icon v-if="isElinkVpnConfigured(row)" v-tooltip:top="'下载OVPN配置文件'" size="small" variant="text"
                    color="primary" :loading="downloadingElinkUuid === row.uuid" @click="handleDownloadElinkOvpn(row)">
                    <v-icon size="20">mdi-download-outline</v-icon>
                  </v-btn>
                  <v-btn icon v-else-if="canConfigureElinkVpn(row)" v-tooltip:top="'配置VPN'" size="small" variant="tonal"
                    color="primary" :loading="configuringElinkUuid === row.uuid" @click="handleConfigureElinkVpn(row)">
                    <v-icon size="20">mdi-vpn</v-icon>
                  </v-btn>
                  <v-btn icon v-tooltip:top="'编辑储能方阵'" size="small" variant="text" color="primary" aria-label="编辑储能方阵"
                    @click="openElinkEdit(row)">
                    <v-icon size="20">mdi-pencil-outline</v-icon>
                  </v-btn>
                  <v-btn icon v-tooltip:top="'删除储能方阵'" size="small" variant="text" color="error" aria-label="删除储能方阵"
                    @click="askDeleteElink(row)">
                    <v-icon size="20">mdi-delete-outline</v-icon>
                  </v-btn>
                </div>
              </div>
              <v-expand-transition>
                <div :id="`elink-expand-${row.id}`" v-show="isElinkExpanded(row.id)" class="elink-expand-body"
                  role="region">
                  <div class="elink-expand-inner px-4 pb-4 d-flex flex-column ga-3">
                    <div class="elink-expand-section min-width-0">
                      <h3 class="subsection-title elink-expand-subtitle mb-2 mt-2">
                        <v-icon size="18" color="medium-emphasis">mdi-vector-link</v-icon>
                        <span>eLink</span>
                      </h3>
                      <div class="elink-meta text-body-2 pa-2 rounded-lg">
                        <div class="elink-meta-inline ga-2 text-left">
                          <span class="text-medium-emphasis flex-shrink-0">型号</span>
                          <v-chip size="small" variant="flat" class="elink-model-chip flex-shrink-0">{{ row.modelTag
                          }}</v-chip>
                          <span class="elink-meta-sep text-medium-emphasis flex-shrink-0" aria-hidden="true">·</span>
                          <span class="text-medium-emphasis flex-shrink-0">UUID</span>
                          <span
                            class="elink-meta-value elink-meta-value--copyable elink-meta-value--nowrap font-weight-medium"
                            :title="row.uuid || ''">{{ row.uuid || '—' }}</span>
                          <v-btn v-if="(row.uuid ?? '').trim()" icon size="x-small" variant="text" color="primary"
                            class="device-clipboard-btn flex-shrink-0" aria-label="复制 UUID"
                            @click.stop="copyElinkClipboard(row.uuid)">
                            <v-icon size="18">mdi-content-copy</v-icon>
                          </v-btn>
                          <template v-if="(row.deviceNetworkId ?? '').trim()">
                            <span class="elink-meta-sep text-medium-emphasis flex-shrink-0" aria-hidden="true">·</span>
                            <span class="text-medium-emphasis flex-shrink-0">设备网络 ID</span>
                            <span class="elink-meta-value elink-meta-value--nowrap font-weight-medium"
                              :title="row.deviceNetworkId">{{ row.deviceNetworkId }}</span>
                          </template>
                          <template v-if="String(row.measuringDistance ?? '').trim()">
                            <span class="elink-meta-sep text-medium-emphasis flex-shrink-0" aria-hidden="true">·</span>
                            <span class="text-medium-emphasis flex-shrink-0">计量点传输距离（米）</span>
                            <span class="elink-meta-value elink-meta-value--nowrap font-weight-medium">{{
                              row.measuringDistance
                            }}</span>
                          </template>
                          <span class="elink-meta-sep text-medium-emphasis flex-shrink-0" aria-hidden="true">·</span>
                          <span class="text-medium-emphasis flex-shrink-0">EMQX Client ID</span>
                          <span
                            class="elink-meta-value elink-meta-value--copyable elink-meta-value--nowrap font-weight-medium"
                            :title="row.emqxClientId || ''">{{ row.emqxClientId || '—' }}</span>
                          <v-btn v-if="(row.emqxClientId ?? '').trim()" icon size="x-small" variant="text"
                            color="primary" class="device-clipboard-btn flex-shrink-0" aria-label="复制 EMQX Client ID"
                            @click.stop="copyElinkClipboard(row.emqxClientId)">
                            <v-icon size="18">mdi-content-copy</v-icon>
                          </v-btn>
                        </div>
                      </div>
                    </div>
                    <div class="elink-expand-section min-width-0">
                      <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-2">
                        <h3 class="subsection-title elink-expand-subtitle mb-0">
                          <v-icon size="18" color="medium-emphasis">mdi-water-outline</v-icon>
                          <span>eLink 水机</span>
                        </h3>
                        <v-btn v-if="!readOnly" color="var(--color-primary)" variant="tonal" size="small" rounded="lg"
                          prepend-icon="mdi-plus" @click.stop="openElinkWaterMachineAdd(row)">
                          新增eLink水机
                        </v-btn>
                      </div>
                      <div v-if="elinkWaterMachinesForExpand(row).length" class="elink-expand-table-scroll">
                        <v-table class="meter-table gm-table bg-transparent" density="comfortable">
                          <thead>
                            <tr>
                              <th>名称</th>
                              <th>产品型号</th>
                              <th>UUID</th>
                              <th>设备 devID</th>
                              <th v-if="!readOnly" class="text-end meter-table-actions">操作</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="wm in elinkWaterMachinesForExpand(row)" :key="wm.key">
                              <td class="text-body-2">{{ wm.name || '—' }}</td>
                              <td class="text-body-2">{{ wm.productModel || '—' }}</td>
                              <td class="elink-expand-table-cell-tight min-width-0">
                                <div class="d-flex align-center ga-1 min-width-0">
                                  <span class="text-body-2 text-truncate min-width-0" :title="wm.uuid || ''">{{
                                    wm.uuid || '—'
                                  }}</span>
                                  <v-btn v-if="(wm.uuid ?? '').trim()" icon size="x-small" variant="text"
                                    color="primary" class="device-clipboard-btn flex-shrink-0" aria-label="复制 UUID"
                                    @click.stop="copyElinkClipboard(wm.uuid)">
                                    <v-icon size="18">mdi-content-copy</v-icon>
                                  </v-btn>
                                </div>
                              </td>
                              <td class="elink-expand-table-cell-tight min-width-0">
                                <div class="d-flex align-center ga-1 min-width-0">
                                  <span class="text-body-2 text-truncate min-width-0" :title="wm.deviceId || ''">{{
                                    wm.deviceId || '—'
                                  }}</span>
                                  <v-btn v-if="(wm.deviceId ?? '').trim()" icon size="x-small" variant="text"
                                    color="primary" class="device-clipboard-btn flex-shrink-0" aria-label="复制设备 devID"
                                    @click.stop="copyElinkClipboard(wm.deviceId)">
                                    <v-icon size="18">mdi-content-copy</v-icon>
                                  </v-btn>
                                </div>
                              </td>
                              <td v-if="!readOnly" class="text-end text-nowrap">
                                <v-btn icon size="small" variant="text" color="primary" aria-label="编辑 eLink 水机"
                                  @click.stop="openElinkWaterMachineEdit(row, wm)">
                                  <v-icon size="20">mdi-pencil-outline</v-icon>
                                </v-btn>
                                <v-btn icon size="small" variant="text" color="error" aria-label="删除 eLink 水机"
                                  @click.stop="askDeleteElinkWaterMachine(row, wm)">
                                  <v-icon size="20">mdi-delete-outline</v-icon>
                                </v-btn>
                              </td>
                            </tr>
                          </tbody>
                        </v-table>
                      </div>
                      <div v-else class="elink-metering-expand-empty text-body-2 text-medium-emphasis pa-3 rounded-lg">
                        暂无水机信息。
                      </div>
                    </div>
                    <div class="elink-expand-section min-width-0">
                      <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-2">
                        <div class="d-flex align-center ga-2 flex-wrap">
                          <h3 class="subsection-title elink-expand-subtitle mb-0">
                            <v-icon size="18" color="medium-emphasis">mdi-cube-outline</v-icon>
                            <span v-if="isDistributedStation">eBlock</span>
                            <span v-else>电池簇</span>
                          </h3>
                          <span v-if="isDistributedStation && hasMultiClusterEblock(row)"
                            class="eblock-cluster-readonly-tip">
                            <v-icon size="14" color="warning">mdi-information-outline</v-icon>
                            电池簇不支持编辑
                          </span>
                        </div>
                        <v-btn v-if="!readOnly" class="text-none" color="var(--color-primary)" variant="tonal"
                          size="small" rounded="lg" prepend-icon="mdi-plus" @click.stop="openEblockAdd(row)"
                          :disabled="!isDistributedStation">
                          新增
                          <span v-if="isDistributedStation">eBlock</span>
                          <span v-else>电池簇</span>
                        </v-btn>
                      </div>

                      <template v-if="isDistributedStation">
                        <v-table v-if="eblocksForRow(row).length"
                          class="elink-expand-table meter-table gm-table bg-transparent min-width-0"
                          density="comfortable">
                          <thead>
                            <tr>
                              <th class="elink-expand-table-col-name">名称</th>
                              <th>型号</th>
                              <th>设备 devID</th>
                              <th>UUID</th>
                              <th>水机名称</th>
                              <th>水机型号</th>
                              <th>水机设备 devID</th>
                              <th>水机 UUID</th>
                              <th>电池簇型号</th>
                              <th>电池簇设备 devID</th>
                              <th>电池簇 UUID</th>
                              <th v-if="!readOnly" class="text-end elink-expand-table-col-actions">操作</th>
                            </tr>
                          </thead>
                          <tbody>
                            <template v-for="dr in eblockDisplayRowsForRow(row)" :key="dr.key">
                              <tr :class="{ 'eblock-cluster-row': !dr.isFirst }">
                                <template v-if="dr.isFirst">
                                  <td class="elink-expand-table-cell-name min-width-0" :rowspan="dr.clusterCount">
                                    <span
                                      class="text-body-2 font-weight-medium text-truncate d-inline-block min-width-0"
                                      :title="dr.eb.name">{{ dr.eb.name }}</span>
                                  </td>
                                  <td class="text-body-2 text-nowrap" :rowspan="dr.clusterCount">{{ dr.eb.modelTag }}
                                  </td>
                                  <td class="elink-expand-table-cell-tight min-width-0" :rowspan="dr.clusterCount">
                                    <div class="d-flex align-center ga-1 min-width-0">
                                      <span class="text-body-2 text-truncate min-width-0"
                                        :title="dr.eb.deviceId || ''">{{
                                          dr.eb.deviceId || '—'
                                        }}</span>
                                      <v-btn v-if="(dr.eb.deviceId ?? '').trim()" icon size="x-small" variant="text"
                                        color="primary" class="device-clipboard-btn flex-shrink-0"
                                        aria-label="复制设备 devID" @click.stop="copyElinkClipboard(dr.eb.deviceId)">
                                        <v-icon size="18">mdi-content-copy</v-icon>
                                      </v-btn>
                                    </div>
                                  </td>
                                  <td class="elink-expand-table-cell-tight min-width-0" :rowspan="dr.clusterCount">
                                    <div class="d-flex align-center ga-1 min-width-0">
                                      <span class="text-body-2 text-truncate min-width-0" :title="dr.eb.uuid || ''">{{
                                        dr.eb.uuid || '—'
                                      }}</span>
                                      <v-btn v-if="(dr.eb.uuid ?? '').trim()" icon size="x-small" variant="text"
                                        color="primary" class="device-clipboard-btn flex-shrink-0" aria-label="复制 UUID"
                                        @click.stop="copyElinkClipboard(dr.eb.uuid)">
                                        <v-icon size="18">mdi-content-copy</v-icon>
                                      </v-btn>
                                    </div>
                                  </td>
                                  <td class="text-body-2 text-nowrap" :rowspan="dr.clusterCount">{{
                                    dr.eb.waterMachineName || '—' }}</td>
                                  <td class="text-body-2 text-nowrap" :rowspan="dr.clusterCount">{{
                                    dr.eb.waterMachineModel || '—' }}</td>
                                  <td class="elink-expand-table-cell-tight min-width-0" :rowspan="dr.clusterCount">
                                    <div class="d-flex align-center ga-1 min-width-0">
                                      <span class="text-body-2 text-truncate min-width-0"
                                        :title="dr.eb.waterMachineDevId || ''">{{
                                          dr.eb.waterMachineDevId || '—'
                                        }}</span>
                                      <v-btn v-if="(dr.eb.waterMachineDevId ?? '').trim()" icon size="x-small"
                                        variant="text" color="primary" class="device-clipboard-btn flex-shrink-0"
                                        aria-label="复制水机设备 devID"
                                        @click.stop="copyElinkClipboard(dr.eb.waterMachineDevId)">
                                        <v-icon size="18">mdi-content-copy</v-icon>
                                      </v-btn>
                                    </div>
                                  </td>
                                  <td class="elink-expand-table-cell-tight min-width-0" :rowspan="dr.clusterCount">
                                    <div class="d-flex align-center ga-1 min-width-0">
                                      <span class="text-body-2 text-truncate min-width-0"
                                        :title="dr.eb.waterMachineUuid || ''">{{
                                          dr.eb.waterMachineUuid || '—'
                                        }}</span>
                                      <v-btn v-if="(dr.eb.waterMachineUuid ?? '').trim()" icon size="x-small"
                                        variant="text" color="primary" class="device-clipboard-btn flex-shrink-0"
                                        aria-label="复制水机 UUID" @click.stop="copyElinkClipboard(dr.eb.waterMachineUuid)">
                                        <v-icon size="18">mdi-content-copy</v-icon>
                                      </v-btn>
                                    </div>
                                  </td>
                                </template>
                                <!-- 电池簇（每簇一行） -->
                                <td class="text-body-2 text-nowrap">
                                  {{ dr.cluster?.productModel || '—' }}
                                </td>
                                <td class="elink-expand-table-cell-tight min-width-0">
                                  <div class="d-flex align-center ga-1 min-width-0">
                                    <span class="text-body-2 text-truncate min-width-0"
                                      :title="dr.cluster?.devId || ''">{{
                                        dr.cluster?.devId || '—'
                                      }}</span>
                                    <v-btn v-if="(dr.cluster?.devId ?? '').trim()" icon size="x-small" variant="text"
                                      color="primary" class="device-clipboard-btn flex-shrink-0"
                                      aria-label="复制电池簇 devID" @click.stop="copyElinkClipboard(dr.cluster.devId)">
                                      <v-icon size="18">mdi-content-copy</v-icon>
                                    </v-btn>
                                  </div>
                                </td>
                                <td class="elink-expand-table-cell-tight min-width-0">
                                  <div class="d-flex align-center ga-1 min-width-0">
                                    <span class="text-body-2 text-truncate min-width-0"
                                      :title="dr.cluster?.uuid || ''">{{
                                        dr.cluster?.uuid || '—'
                                      }}</span>
                                    <v-btn v-if="(dr.cluster?.uuid ?? '').trim()" icon size="x-small" variant="text"
                                      color="primary" class="device-clipboard-btn flex-shrink-0" aria-label="复制电池簇 UUID"
                                      @click.stop="copyElinkClipboard(dr.cluster.uuid)">
                                      <v-icon size="18">mdi-content-copy</v-icon>
                                    </v-btn>
                                  </div>
                                </td>
                                <td v-if="!readOnly && dr.isFirst" class="text-end text-nowrap"
                                  :rowspan="dr.clusterCount">
                                  <!-- 集中式电站 禁止操作 -->
                                  <v-btn icon size="small" variant="text" color="primary" aria-label="编辑 eBlock"
                                    @click.stop="openEblockEdit(row, dr.eb)" :disabled="!isDistributedStation">
                                    <v-icon size="20">mdi-pencil-outline</v-icon>
                                  </v-btn>
                                  <!-- 集中式电站 禁止操作 -->
                                  <v-btn icon size="small" variant="text" color="error" aria-label="删除 eBlock"
                                    @click.stop="askDeleteEblock(row, dr.eb)" :disabled="!isDistributedStation">
                                    <v-icon size="20">mdi-delete-outline</v-icon>
                                  </v-btn>
                                </td>
                              </tr>
                            </template>
                          </tbody>
                        </v-table>
                        <div v-else
                          class="elink-metering-expand-empty text-body-2 text-medium-emphasis pa-3 rounded-lg">
                          暂无 eBlock，请点击「新增 eBlock」维护名称、型号与设备信息。
                        </div>
                      </template>

                      <!-- 集中式电站  显示电池簇 -->
                      <template v-else>
                        <v-table v-if="clusterForRow(row).length"
                          class="elink-expand-table meter-table gm-table bg-transparent min-width-0"
                          density="comfortable">
                          <thead>
                            <tr>
                              <th class="elink-expand-table-col-name">名称</th>
                              <th>型号</th>
                              <th>设备 devID</th>
                              <th>UUID</th>
                              <th v-if="!readOnly" class="text-end elink-expand-table-col-actions">操作</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="eb in clusterForRow(row)" :key="eb.key">
                              <td class="elink-expand-table-cell-name min-width-0">
                                <span class="text-body-2 font-weight-medium text-truncate d-inline-block min-width-0"
                                  :title="eb.name">{{ eb.name }}</span>
                              </td>
                              <td class="text-body-2 text-nowrap">{{ eb.modelTag }}</td>
                              <td class="elink-expand-table-cell-tight min-width-0">
                                <div class="d-flex align-center ga-1 min-width-0">
                                  <span class="text-body-2 text-truncate min-width-0" :title="eb.deviceId || ''">{{
                                    eb.deviceId || '—'
                                  }}</span>
                                  <v-btn v-if="(eb.deviceId ?? '').trim()" icon size="x-small" variant="text"
                                    color="primary" class="device-clipboard-btn flex-shrink-0" aria-label="复制设备 devID"
                                    @click.stop="copyElinkClipboard(eb.deviceId)">
                                    <v-icon size="18">mdi-content-copy</v-icon>
                                  </v-btn>
                                </div>
                              </td>
                              <td class="elink-expand-table-cell-tight min-width-0">
                                <div class="d-flex align-center ga-1 min-width-0">
                                  <span class="text-body-2 text-truncate min-width-0" :title="eb.uuid || ''">{{
                                    eb.uuid || '—'
                                  }}</span>
                                  <v-btn v-if="(eb.uuid ?? '').trim()" icon size="x-small" variant="text"
                                    color="primary" class="device-clipboard-btn flex-shrink-0" aria-label="复制 UUID"
                                    @click.stop="copyElinkClipboard(eb.uuid)">
                                    <v-icon size="18">mdi-content-copy</v-icon>
                                  </v-btn>
                                </div>
                              </td>


                              <td v-if="!readOnly" class="text-end text-nowrap">
                                <!-- 集中式电站 禁止操作 -->
                                <v-btn icon size="small" variant="text" color="primary" aria-label="编辑"
                                  @click.stop="openEblockEdit(row, eb)" :disabled="!isDistributedStation">
                                  <v-icon size="20">mdi-pencil-outline</v-icon>
                                </v-btn>
                                <!-- 集中式电站 禁止操作 -->
                                <v-btn icon size="small" variant="text" color="error" aria-label="删除"
                                  @click.stop="askDeleteEblock(row, eb)" :disabled="!isDistributedStation">
                                  <v-icon size="20">mdi-delete-outline</v-icon>
                                </v-btn>
                              </td>
                            </tr>
                          </tbody>
                        </v-table>
                        <div v-else
                          class="elink-metering-expand-empty text-body-2 text-medium-emphasis pa-3 rounded-lg">
                          暂无 电池簇，请点击「新增 电池簇」维护名称、型号与设备信息。
                        </div>
                      </template>


                    </div>
                    <div class="elink-expand-section min-width-0">
                      <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-2">
                        <h3 class="subsection-title elink-expand-subtitle mb-0">
                          <v-icon size="18" color="medium-emphasis">mdi-meter-electric-outline</v-icon>
                          <span>计量电表</span>
                        </h3>
                        <v-btn v-if="!readOnly" color="var(--color-primary)" variant="tonal" size="small" rounded="lg"
                          prepend-icon="mdi-plus" @click.stop="openElinkMeterAdd(row)">
                          新增计量电表
                        </v-btn>
                      </div>
                      <div v-if="meteringMetersForExpand(row).length" class="elink-expand-table-scroll">
                        <v-table class="meter-table gm-table bg-transparent" density="comfortable">
                          <thead>
                            <tr>
                              <th>名称</th>
                              <th>UUID</th>
                              <th>设备 devID</th>
                              <th v-if="!readOnly" class="text-end meter-table-actions">操作</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="mm in meteringMetersForExpand(row)" :key="mm.key">
                              <td class="text-body-2">{{ mm.name || '—' }}</td>
                              <td class="elink-expand-table-cell-tight min-width-0">
                                <div class="d-flex align-center ga-1 min-width-0">
                                  <span class="text-body-2 text-truncate min-width-0" :title="mm.uuid || ''">{{
                                    mm.uuid || '—'
                                  }}</span>
                                  <v-btn v-if="(mm.uuid ?? '').trim()" icon size="x-small" variant="text"
                                    color="primary" class="device-clipboard-btn flex-shrink-0" aria-label="复制 UUID"
                                    @click.stop="copyElinkClipboard(mm.uuid)">
                                    <v-icon size="18">mdi-content-copy</v-icon>
                                  </v-btn>
                                </div>
                              </td>
                              <td class="elink-expand-table-cell-tight min-width-0">
                                <div class="d-flex align-center ga-1 min-width-0">
                                  <span class="text-body-2 text-truncate min-width-0" :title="mm.deviceId || ''">{{
                                    mm.deviceId || '—'
                                  }}</span>
                                  <v-btn v-if="(mm.deviceId ?? '').trim()" icon size="x-small" variant="text"
                                    color="primary" class="device-clipboard-btn flex-shrink-0" aria-label="复制设备 devID"
                                    @click.stop="copyElinkClipboard(mm.deviceId)">
                                    <v-icon size="18">mdi-content-copy</v-icon>
                                  </v-btn>
                                </div>
                              </td>
                              <td v-if="!readOnly" class="text-end text-nowrap">
                                <v-btn icon size="small" variant="text" color="primary" aria-label="编辑计量电表"
                                  @click.stop="openElinkMeterEdit(row, mm)">
                                  <v-icon size="20">mdi-pencil-outline</v-icon>
                                </v-btn>
                                <v-btn icon size="small" variant="text" color="error" aria-label="删除计量电表"
                                  @click.stop="askDeleteElinkMeter(row, mm)">
                                  <v-icon size="20">mdi-delete-outline</v-icon>
                                </v-btn>
                              </td>
                            </tr>
                          </tbody>
                        </v-table>
                      </div>
                      <div v-else-if="meteringMeterCount(row) > 0"
                        class="elink-metering-expand-empty text-body-2 text-medium-emphasis pa-3 rounded-lg">
                        已配置 {{ meteringMeterCount(row) }} 个计量电表，可继续点击新增计量电表维护。
                      </div>
                      <div v-else class="elink-metering-expand-empty text-body-2 text-medium-emphasis pa-3 rounded-lg">
                        暂无计量电表，请点击新增计量电表。
                      </div>
                    </div>
                  </div>
                </div>
              </v-expand-transition>
            </div>
          </div>
        </section>

        <section id="device-section-meter" class="device-section" aria-labelledby="device-section-meter-title">
          <div class="d-flex align-center justify-space-between flex-wrap ga-2 device-section-head">
            <h2 id="device-section-meter-title" class="section-title device-section-title mb-0">电表</h2>
            <v-btn v-if="!readOnly" color="var(--color-primary)" variant="tonal" size="small" rounded="lg"
              prepend-icon="mdi-plus" @click="openMeterAdd">
              新增电表
            </v-btn>
          </div>
          <v-table class="meter-table gm-table bg-transparent" density="comfortable">
            <thead>
              <tr>
                <th>电表名称</th>
                <th>产品型号</th>
                <th>电表类型</th>
                <th>UUID</th>
                <th>厂家名称</th>
                <th>安装位置</th>
                <th>电表变比</th>
                <th v-if="!readOnly" class="text-end meter-table-actions">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="m in meterRows" :key="m.id">
                <td>{{ m.meterName }}</td>
                <td>{{ m.productModel || '—' }}</td>
                <td>{{ meterTypeLabel(m.meterType) }}</td>
                <td>{{ m.uuid || '—' }}</td>
                <td>{{ m.factoryName || '—' }}</td>
                <td class="text-body-2">{{ productLocationLabel(m.productLocation) }}</td>
                <td class="text-body-2">{{ m.productRatio ?? '—' }}</td>
                <td v-if="!readOnly" class="text-end text-nowrap">
                  <v-btn icon size="small" variant="text" color="primary" aria-label="编辑" @click="openMeterEdit(m)">
                    <v-icon size="20">mdi-pencil-outline</v-icon>
                  </v-btn>
                  <v-btn icon size="small" variant="text" color="error" aria-label="删除" @click="askDeleteMeter(m)">
                    <v-icon size="20">mdi-delete-outline</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-table>
        </section>

        <section v-if="visibleDeviceSections.some((item) => item.key === 'transformer')" id="device-section-transformer"
          class="device-section" aria-labelledby="device-section-transformer-title">
          <div class="d-flex align-center justify-space-between flex-wrap ga-2 device-section-head">
            <h2 id="device-section-transformer-title" class="section-title device-section-title mb-0">变压器</h2>
            <v-btn v-if="!readOnly" color="var(--color-primary)" variant="tonal" size="small" rounded="lg"
              prepend-icon="mdi-plus" @click="openTransformerAdd">
              新增变压器
            </v-btn>
          </div>
          <v-table class="meter-table gm-table bg-transparent" density="comfortable">
            <thead>
              <tr>
                <th>变压器名称</th>
                <th>产品型号</th>
                <th>UUID</th>
                <th>出厂编号</th>
                <th>出厂时间</th>
                <th>额定容量</th>
                <th>电压等级</th>
                <th>输入电压</th>
                <th>输出电压</th>
                <th>绝缘散热介质</th>
                <th>铁芯结构</th>
                <th v-if="!readOnly" class="text-end meter-table-actions">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="t in transformerRows" :key="t.id ?? t.uuid">
                <td>{{ t.transformerName }}</td>
                <td>{{ t.productModel || '—' }}</td>
                <td>{{ t.uuid || '—' }}</td>
                <td>{{ t.serialNumber || '—' }}</td>
                <td>{{ t.manufactureTime || '—' }}</td>
                <td>{{ t.ratedCapacity || '—' }}</td>
                <td>{{ t.voltageLevel || '—' }}</td>
                <td>{{ t.inputVoltage || '—' }}</td>
                <td>{{ t.outputVoltage || '—' }}</td>
                <td>{{ t.medium || '—' }}</td>
                <td>{{ t.coreStructure || '—' }}</td>
                <td v-if="!readOnly" class="text-end text-nowrap meter-table-actions">
                  <v-btn icon size="small" variant="text" color="primary" aria-label="编辑变压器"
                    @click="openTransformerEdit(t)">
                    <v-icon size="20">mdi-pencil-outline</v-icon>
                  </v-btn>
                  <v-btn icon size="small" variant="text" color="error" aria-label="删除变压器"
                    @click="askDeleteTransformer(t)">
                    <v-icon size="20">mdi-delete-outline</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-table>
        </section>
        <section v-if="visibleDeviceSections.some((item) => item.key === 'conditioner')" id="device-section-conditioner"
          class="device-section" aria-labelledby="device-section-conditioner-title">
          <div class="d-flex align-center justify-space-between flex-wrap ga-2 device-section-head">
            <h2 id="device-section-conditioner-title" class="section-title device-section-title mb-0">水机</h2>
            <v-btn v-if="!readOnly" color="var(--color-primary)" variant="tonal" size="small" rounded="lg"
              prepend-icon="mdi-plus" @click="openConditionerAdd">
              新增水机
            </v-btn>
          </div>
          <v-table class="meter-table gm-table bg-transparent" density="comfortable">
            <thead>
              <tr>
                <th>水机名称</th>
                <th>产品型号</th>
                <th>设备devID</th>
                <th>UUID</th>
                <th>厂家名称</th>
                <th v-if="!readOnly" class="text-end meter-table-actions">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in conditionerRows" :key="c.id">
                <td>{{ c.waterMachineName }}</td>
                <td>{{ c.productModel || '—' }}</td>
                <td>{{ c.devId || '—' }}</td>
                <td>{{ c.uuid }}</td>
                <td>{{ c.factoryName || '—' }}</td>
                <td v-if="!readOnly" class="text-end text-nowrap meter-table-actions">
                  <v-btn icon size="small" variant="text" color="primary" aria-label="编辑水机"
                    @click="openConditionerEdit(c)">
                    <v-icon size="20">mdi-pencil-outline</v-icon>
                  </v-btn>
                  <v-btn icon size="small" variant="text" color="error" aria-label="删除水机"
                    @click="askDeleteConditioner(c)">
                    <v-icon size="20">mdi-delete-outline</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-table>
        </section>

        <section v-if="visibleDeviceSections.some((item) => item.key === 'pv')" id="device-section-pv"
          class="device-section" aria-labelledby="device-section-pv-title">
          <div class="d-flex align-center justify-space-between flex-wrap ga-2 device-section-head">
            <h2 id="device-section-pv-title" class="section-title device-section-title mb-0">光伏</h2>
            <v-btn v-if="!readOnly" color="var(--color-primary)" variant="tonal" size="small" rounded="lg"
              prepend-icon="mdi-plus" @click="openPvAdd">
              新增光伏
            </v-btn>
          </div>
          <v-table class="meter-table gm-table bg-transparent" density="comfortable">
            <thead>
              <tr>
                <th>光伏名称</th>
                <th>产品型号</th>
                <th>UUID</th>
                <th>最大直流电压(V)</th>
                <th>最大直流功率(kW)</th>
                <th>额定输出功率(kW)</th>
                <th>最大输出功率(kW)</th>
                <th>额定电网电压(V)</th>
                <th>额定电网频率(Hz)</th>
                <th v-if="!readOnly" class="text-end meter-table-actions">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in pvRows" :key="p.id">
                <td>{{ p.photovoltaicName }}</td>
                <td>{{ p.productModel || '—' }}</td>
                <td>{{ p.uuid || '—' }}</td>
                <td>{{ p.maxDcVoltage || '—' }}</td>
                <td>{{ p.maxDcPower || '—' }}</td>
                <td>{{ p.ratedOutputPower || '—' }}</td>
                <td>{{ p.maxOutputPower || '—' }}</td>
                <td>{{ p.ratedGridVoltage || '—' }}</td>
                <td>{{ p.ratedGridFrequency || '—' }}</td>
                <td v-if="!readOnly" class="text-end text-nowrap meter-table-actions">
                  <v-btn icon size="small" variant="text" color="primary" aria-label="编辑光伏" @click="openPvEdit(p)">
                    <v-icon size="20">mdi-pencil-outline</v-icon>
                  </v-btn>
                  <v-btn icon size="small" variant="text" color="error" aria-label="删除光伏" @click="askDeletePv(p)">
                    <v-icon size="20">mdi-delete-outline</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-table>
        </section>

        <section v-if="visibleDeviceSections.some((item) => item.key === 'pile')" id="device-section-pile"
          class="device-section device-section--last" aria-labelledby="device-section-pile-title">
          <div class="d-flex align-center justify-space-between flex-wrap ga-2 device-section-head">
            <h2 id="device-section-pile-title" class="section-title device-section-title mb-0">充电桩</h2>
            <v-btn v-if="!readOnly" color="var(--color-primary)" variant="tonal" size="small" rounded="lg"
              prepend-icon="mdi-plus" @click="openPileAdd">
              新增充电桩
            </v-btn>
          </div>
          <v-table class="meter-table gm-table bg-transparent" density="comfortable">
            <thead>
              <tr>
                <th>充电桩名称</th>
                <th>产品型号</th>
                <th>UUID</th>
                <th>输入电压(V)</th>
                <th>输出电压(V)</th>
                <th>额定输出功率(kW)</th>
                <th>满载功率因数</th>
                <th>充电接口类型</th>
                <th>充电模式</th>
                <th v-if="!readOnly" class="text-end meter-table-actions">操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in pileRows" :key="p.id">
                <td>{{ p.chargePileName }}</td>
                <td>{{ p.productModel || '—' }}</td>
                <td>{{ p.uuid || '—' }}</td>
                <td>{{ p.inputVoltage || '—' }}</td>
                <td>{{ p.outputVoltage || '—' }}</td>
                <td>{{ p.ratedOutputPower || '—' }}</td>
                <td>{{ p.fullLoadPower || '—' }}</td>
                <td>{{ p.chargeInterface || '—' }}</td>
                <td>{{ pileChargeModeLabel(p.chargeMode) }}</td>
                <td v-if="!readOnly" class="text-end text-nowrap meter-table-actions">
                  <v-btn icon size="small" variant="text" color="primary" aria-label="编辑充电桩" @click="openPileEdit(p)">
                    <v-icon size="20">mdi-pencil-outline</v-icon>
                  </v-btn>
                  <v-btn icon size="small" variant="text" color="error" aria-label="删除充电桩" @click="askDeletePile(p)">
                    <v-icon size="20">mdi-delete-outline</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-table>
        </section>
      </main>
    </div>

    <ElinkDialog v-model="elinkDialog.open" :is-edit="elinkDialog.isEdit" :submitting="elinkDialog.submitting"
      :is-distributed-station="isDistributedStation" :galaxy-draft="galaxyDraft" :elink-draft="elinkDraft"
      :filtered-eblock-model-options="filteredEblockModelOptions"
      :show-draft-eblock-water-machine-fields="shouldShowDraftEblockWaterMachineFields"
      :conditioner-model-options="conditionerModelOptions" :conditioner-model-loading="conditionerModelLoading"
      @close="closeElinkDialog" @submit="submitElink" @add-water-machine="addDraftElinkWaterMachine()"
      @update-water-machine-model="updateDraftElinkWaterMachineModel"
      @remove-water-machine="removeDraftElinkWaterMachine" @add-metering-meter="addElinkMeteringMeter"
      @remove-metering-meter="removeElinkMeteringMeter" />

    <MeterDialog v-model="meterDialog.open" :is-edit="meterDialog.isEdit" :submitting="meterSubmitting"
      :elink-id="meterDialog.elinkId" :draft="meterDraft" :meter-model-options="meterModelOptions"
      :meter-model-loading="meterModelLoading" :before-upload="beforeMeterImageUpload"
      :http-request="requestMeterImageUpload" @close="closeMeterDialog" @submit="submitMeter"
      @model-change="onMeterModelChange" @clear-image="clearMeterImage" />

    <TransformerDialog v-model="transformerDialog.open" :is-edit="transformerDialog.isEdit"
      :submitting="transformerSubmitting" :draft="transformerDraft"
      :manufacture-date-value="transformerManufactureDateValue" @close="closeTransformerDialog"
      @submit="submitTransformer" @date-change="handleTransformerManufactureDateChange" />

    <EblockDialog v-model="eblockDialog.open" :is-edit="eblockDialog.isEdit" :draft="eblockDraft"
      :filtered-eblock-model-options="filteredEblockModelOptions"
      :show-water-machine-fields="shouldShowEblockWaterMachineFields"
      :conditioner-model-options="conditionerModelOptions" :conditioner-model-loading="conditionerModelLoading"
      @close="closeEblockDialog" @submit="submitEblock" @water-machine-model-change="onEblockWaterMachineModelChange" />

    <ElinkWaterMachineDialog v-model="elinkWaterMachineDialog.open" :is-edit="elinkWaterMachineDialog.isEdit"
      :submitting="elinkWaterMachineDialog.submitting" :draft="elinkWaterMachineDraft"
      :conditioner-model-options="conditionerModelOptions" :conditioner-model-loading="conditionerModelLoading"
      @close="closeElinkWaterMachineDialog" @submit="submitElinkWaterMachine" />

    <PvDialog v-model="pvDialog.open" :is-edit="pvDialog.isEdit" :submitting="pvSubmitting" :draft="pvDraft"
      :pv-model-options="pvModelOptions" :pv-model-loading="pvModelLoading"
      :pile-parent-equipment-options="pileParentEquipmentOptions" @close="closePvDialog" @submit="submitPv"
      @model-change="onPvModelChange" />

    <PileDialog v-model="pileDialog.open" :is-edit="pileDialog.isEdit" :submitting="pileSubmitting" :draft="pileDraft"
      :pile-model-options="pileModelOptions" :pile-model-loading="pileModelLoading"
      :pile-parent-equipment-options="pileParentEquipmentOptions" @close="closePileDialog" @submit="submitPile"
      @model-change="onPileModelChange" />

    <DeleteDialog v-model="deleteDialog.open" :kind="deleteDialog.kind" :target="deleteDialog.target"
      @close="closeDeleteDialog" @confirm="confirmDelete" />
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import dayjs from 'dayjs'
import { eui } from 'ems-ui'
import { Box, Gauge, RadioTower, Sun, BatteryCharging } from 'lucide-vue-next'
import DataCard from '@/components/DataCard/index.vue'
import {
  addChargePile,
  updateChargePile,
  getChargePileList,
  delChargePileList,
} from '@/api/device/chargePile'
import {
  addPv,
  updatePv,
  getPvList,
  delPvList,
} from '@/api/device/pv'
import {
  addMeter,
  updateMeter,
  getMeterList,
  delMeterList,
} from '@/api/device/meter'
import { addBlockV3, updateBlockV3, delEBlockV3 } from '@/api/device/eblock'
import {
  addTransformer,
  updateTransformer,
  getTransformerList,
  delTransformerList,
} from '@/api/device/transformer'
import { listChargePileModel, listPhotovoltaicModel, listWaterMachineModel, listMeterModel, listEBlockModel } from '@/api/device/model'
import { addWaterMachine, updateWaterMachine, getWaterMachineList, delWaterMachineList } from '@/api/device/waterMachine'
import { detail as getStationDetail } from '@/api/device/station'
import {
  addElinkV3,
  updateElinkV3,
  delElinkV3,
  getElinkListV3,
  configureElinkVpn,
  downloadElinkOvpn,
} from '@/api/modules/device/elink'
import { addGalaxy, updateGalaxy, getGalaxyTree, deleteGalaxy } from '@/api/modules/device/galaxy'
import { uploadFile } from '@/api/common'
import { useStationAddBasic } from '../useStationAddBasic'
import * as uuidUtil from './step-device/utils/uuid'
import {
  meterTypeOptions,
  meterUuidConfigMap,
  meterTypeLabel,
  productLocationOptions,
  productLocationLabel,
  elinkModelOptions,
  galaxyModelPattern,
  pileChargeModeOptions,
  pileChargeModeLabel,
} from './step-device/utils/constants'
import {
  genSimpleId,
  newEblockKey,
  newMeteringMeterKey,
  newWaterMachineKey,
  parsePositiveOrder,
  nextOrderFromList,
  inferEblockSeq,
  saveBlobFile,
  normalizeUploadedFileUrl,
  formatTransformerManufactureTime,
} from './step-device/utils/helpers'
import {
  inferElinkOrder,
  inferEblockOrder,
  inferElinkWaterMachineOrder,
  inferElinkMeterOrder,
  nextEblockOrder,
  nextElinkWaterMachineOrder,
  nextElinkMeterOrder,
} from './step-device/utils/uuid'
import ElinkDialog from './step-device/dialogs/ElinkDialog.vue'
import MeterDialog from './step-device/dialogs/MeterDialog.vue'
import TransformerDialog from './step-device/dialogs/TransformerDialog.vue'
import EblockDialog from './step-device/dialogs/EblockDialog.vue'
import ElinkWaterMachineDialog from './step-device/dialogs/ElinkWaterMachineDialog.vue'
import PvDialog from './step-device/dialogs/PvDialog.vue'
import PileDialog from './step-device/dialogs/PileDialog.vue'
import DeleteDialog from './step-device/dialogs/DeleteDialog.vue'

const props = defineProps({
  readOnly: { type: Boolean, default: false },
  stationId: { type: [String, Number], default: null },
})

const { form: basicForm } = useStationAddBasic()

// —— 依赖 props.stationId 的 UUID builder wrapper（实现已迁移至 step-device/utils/uuid.js）——
const buildElinkUuid = (order) => uuidUtil.buildElinkUuid(props.stationId, order)
const buildGalaxyUuid = (order) => uuidUtil.buildGalaxyUuid(props.stationId, order)
const buildEblockUuid = (elinkOrder, eblockOrder) =>
  uuidUtil.buildEblockUuid(props.stationId, elinkOrder, eblockOrder)
const buildEblockWaterMachineUuid = (elinkOrder, eblockOrder, waterOrder) =>
  uuidUtil.buildEblockWaterMachineUuid(props.stationId, elinkOrder, eblockOrder, waterOrder)
const buildEblockBatteryClusterUuid = (elinkOrder, eblockOrder, clusterOrder) =>
  uuidUtil.buildEblockBatteryClusterUuid(props.stationId, elinkOrder, eblockOrder, clusterOrder)
const buildEblockBatteryClusterDevId = (elinkOrder, eblockOrder, clusterOrder) =>
  uuidUtil.buildEblockBatteryClusterDevId(elinkOrder, eblockOrder, clusterOrder)
const buildElinkWaterMachineUuid = (elinkOrder, waterOrder) =>
  uuidUtil.buildElinkWaterMachineUuid(props.stationId, elinkOrder, waterOrder)
const buildElinkMeterUuid = (elinkOrder, meterOrder) =>
  uuidUtil.buildElinkMeterUuid(props.stationId, elinkOrder, meterOrder)
const buildMeterUuid = (meterType, order) =>
  uuidUtil.buildMeterUuid(props.stationId, meterUuidConfigMap, meterType, order)
const buildTransformerUuid = (order) => uuidUtil.buildTransformerUuid(props.stationId, order)
const buildPvUuid = (order) => uuidUtil.buildPvUuid(props.stationId, order)
const buildChargePileUuid = (order) => uuidUtil.buildChargePileUuid(props.stationId, order)
const buildWaterMachineMeta = (elinkIdx, seq, productModel) =>
  uuidUtil.buildWaterMachineMeta(props.stationId, elinkIdx, seq, productModel)

const deviceSummary = reactive({
  maxPowerKw: 0,
  capacityKwh: 0,
})

const stationBaseInfo = ref({

})

// 是否为分布式电站
const isDistributedStation = computed(() => {
  return stationBaseInfo.value.energyStorageType === 1
})

async function loadStationSummary() {
  if (!props.stationId) {
    stationDeviceFlag.value = 0
    return
  }
  try {
    const res = await getStationDetail(props.stationId)
    const station = res?.data?.station || {}
    stationDeviceFlag.value = Number(station?.deviceFlag) || 0
    stationBaseInfo.value = station
  } catch (err) {
    console.error('获取电站摘要失败:', err)
    stationDeviceFlag.value = 0
  }
}

const stationDeviceFlag = ref(0)

const deviceSectionConfigs = [
  { key: 'ess', label: '储能系统', icon: Box },
  { key: 'meter', label: '电表', icon: Gauge },
  { key: 'transformer', label: '变压器', icon: RadioTower, bit: 4 },
  { key: 'pv', label: '光伏', icon: Sun, bit: 1 },
  { key: 'pile', label: '充电桩', icon: BatteryCharging, bit: 2 },
]

const visibleDeviceSections = computed(() => deviceSectionConfigs.filter((item) => {
  if (item.bit == null) return true
  return (stationDeviceFlag.value & (1 << item.bit)) !== 0
}))

const deviceNavItems = computed(() => visibleDeviceSections.value)

const deviceNavKey = ref('ess')
const deviceMainScrollRef = ref(null)
const deviceNavLockKey = ref('')

let deviceScrollRaf = 0
let deviceMainScrollCleanup = null

function clearDeviceNavLock() {
  if (deviceNavLockKey.value) deviceNavLockKey.value = ''
}

function syncDeviceNavFromScroll() {
  const navList = deviceNavItems.value
  if (!navList.length) {
    deviceNavKey.value = 'ess'
    return
  }

  if (deviceNavLockKey.value && navList.some((item) => item.key === deviceNavLockKey.value)) {
    deviceNavKey.value = deviceNavLockKey.value
    return
  }

  const root = deviceMainScrollRef.value
  if (!root) {
    if (!navList.some((item) => item.key === deviceNavKey.value)) {
      deviceNavKey.value = navList[0].key
    }
    return
  }

  const maxScrollTop = root.scrollHeight - root.clientHeight
  if (maxScrollTop <= 0) {
    deviceNavKey.value = navList[0].key
    return
  }

  if (root.scrollTop <= 4) {
    deviceNavKey.value = navList[0].key
    return
  }

  const rootTop = root.getBoundingClientRect().top
  const anchor = Math.min(140, Math.max(48, root.clientHeight * 0.28))
  let activeKey = navList[0].key

  for (const item of navList) {
    const section = root.querySelector(`#device-section-${item.key}`)
    if (!section) continue
    const top = section.getBoundingClientRect().top - rootTop
    const bottom = section.getBoundingClientRect().bottom - rootTop
    if (top <= anchor && bottom > anchor) {
      activeKey = item.key
      break
    }
    if (top <= anchor) {
      activeKey = item.key
    }
  }

  deviceNavKey.value = activeKey
}

function onDeviceMainScroll() {
  if (deviceScrollRaf) return
  deviceScrollRaf = requestAnimationFrame(() => {
    deviceScrollRaf = 0
    syncDeviceNavFromScroll()
  })
}

function teardownDeviceMainScroll() {
  deviceMainScrollCleanup?.()
  deviceMainScrollCleanup = null
}

function setupDeviceMainScroll() {
  teardownDeviceMainScroll()
  const el = deviceMainScrollRef.value
  if (!el) return
  el.addEventListener('scroll', onDeviceMainScroll, { passive: true })
  el.addEventListener('wheel', clearDeviceNavLock, { passive: true })
  el.addEventListener('touchstart', clearDeviceNavLock, { passive: true })
  deviceMainScrollCleanup = () => {
    el.removeEventListener('scroll', onDeviceMainScroll)
    el.removeEventListener('wheel', clearDeviceNavLock)
    el.removeEventListener('touchstart', clearDeviceNavLock)
  }
}

function scrollDeviceSection(key) {
  deviceNavLockKey.value = key
  deviceNavKey.value = key
  const root = deviceMainScrollRef.value
  const el = root?.querySelector(`#device-section-${key}`)
  if (!root || !el) return
  const pad = 8
  const delta = el.getBoundingClientRect().top - root.getBoundingClientRect().top - pad
  root.scrollTo({ top: root.scrollTop + delta, behavior: 'smooth' })
  window.setTimeout(() => {
    if (deviceNavLockKey.value === key) syncDeviceNavFromScroll()
  }, 320)
}

onMounted(() => {
  nextTick(() => {
    setupDeviceMainScroll()
    syncDeviceNavFromScroll()
  })
  loadEblockModels()
  loadStationSummary()
  loadElinkList()
  loadTransformerList()
  loadMeterList()
  loadPileList()
  loadPvList()
})

watch(
  () => props.stationId,
  () => {
    loadEblockModels()
    loadStationSummary()
    loadElinkList()
    loadTransformerList()
    loadMeterList()
    loadPileList()
    loadPvList()
  },
)

watch(
  () => deviceNavItems.value.map((item) => item.key).join(','),
  (value) => {
    const firstKey = deviceNavItems.value[0]?.key || 'ess'
    clearDeviceNavLock()
    if (!value) {
      deviceNavKey.value = 'ess'
      return
    }
    if (!deviceNavItems.value.some((item) => item.key === deviceNavKey.value)) {
      deviceNavKey.value = firstKey
    }
    nextTick(() => syncDeviceNavFromScroll())
  },
  { immediate: true },
)

onUnmounted(() => {
  teardownDeviceMainScroll()
})

const elinkRows = ref([])
const configuringElinkUuid = ref('')
const downloadingElinkUuid = ref('')

const meterRows = ref([])

const transformerRows = ref([])
const pvRows = ref([])

const pileRows = ref([])

function sumEblockMetricFromModel(field) {
  return elinkRows.value.reduce((total, row) => {
    const eblockList = Array.isArray(row?.eblocks) ? row.eblocks : []
    const rowTotal = eblockList.reduce((sum, eb) => {
      const modelTag = normalizeEblockModel(eb?.modelTag ?? eb?.productModel)
      const metrics = getEblockModelMetrics(modelTag)
      const value = Number(metrics?.[field]) || 0
      return sum + value
    }, 0)
    return total + rowTotal
  }, 0)
}

const deviceSummaryCards = computed(() => {
  const eblockTotal = elinkRows.value.reduce((acc, r) => acc + eblockCountForRow(r), 0)
  return [
    { key: 'power', value: sumEblockMetricFromModel('maxPower'), label: '装机功率 (kW)', unit: 'kW', icon: 'mdi-lightning-bolt-outline' },
    { key: 'cap', value: sumEblockMetricFromModel('capacity'), label: '装机容量 (kWh)', unit: 'kWh', icon: 'mdi-battery-charging-high' },
    { key: 'elink', value: elinkRows.value.length, label: '储能方阵数量', unit: '', icon: 'mdi-view-module-outline' },
    { key: 'eblock', value: eblockTotal, label: 'eBlock 数量', unit: '', icon: 'mdi-cube-outline' },
  ]
})

const meterModelOptions = ref([])
const meterModelLoading = ref(false)
const meterModelRaw = ref([])

function normalizeMeterModel(v) {
  return typeof v === 'string' ? v.trim() : ''
}

async function loadMeterModels() {
  if (meterModelOptions.value.length || meterModelLoading.value) return
  meterModelLoading.value = true
  try {
    const res = await listMeterModel({ pageNum: 1, pageSize: 200 })
    const list = res?.rows ?? res?.data?.rows ?? res?.data ?? []
    meterModelRaw.value = Array.isArray(list) ? list : []
    meterModelOptions.value = meterModelRaw.value
      .map((m) => ({
        title: m.productModel || m.modelName || '',
        value: m.productModel || m.modelName || '',
        raw: m,
      }))
      .filter((o) => o.value)
  } catch (err) {
    console.error('拉取电表型号失败:', err)
  } finally {
    meterModelLoading.value = false
  }
}

async function loadEblockModels() {
  if (eblockModelOptions.value.length || eblockModelLoading.value) return
  eblockModelLoading.value = true
  try {
    const res = await listEBlockModel({ pageNum: 1, pageSize: 200 })
    const list = res?.rows ?? res?.data?.rows ?? res?.data ?? []
    eblockModelRaw.value = Array.isArray(list) ? list : []
    eblockModelOptions.value = eblockModelRaw.value
      .map((m) => ({
        title: m.productModel || m.modelName || '',
        value: m.productModel || m.modelName || '',
        raw: m,
      }))
      .filter((o) => o.value)
  } catch (err) {
    console.error('拉取 eBlock 型号失败:', err)
  } finally {
    eblockModelLoading.value = false
  }
}

function onMeterModelChange(val) {
  const matched = meterModelRaw.value.find((m) => (m.productModel || m.modelName) === val)
  if (!matched) {
    clearMeterDerivedFields()
    return
  }
  meterDraft.factoryName = matched.factoryName ?? ''
  if (!meterDraft.productRatio && matched.productRatio != null) {
    meterDraft.productRatio = Number(matched.productRatio) || ''
  }
}

function clearMeterDerivedFields() {
  meterDraft.factoryName = ''
}

const eblockModelOptions = ref([])
const eblockModelLoading = ref(false)
const eblockModelRaw = ref([])

const filteredEblockModelOptions = computed(() => {
  const stationType = String(basicForm.stationType || '').trim()
  if (stationType === 'centralized') {
    return eblockModelOptions.value.filter((item) => galaxyModelPattern.test(String(item.value || '').trim()))
  }
  return eblockModelOptions.value.filter((item) => !galaxyModelPattern.test(String(item.value || '').trim()))
})

const defaultEblockModel = computed(() => filteredEblockModelOptions.value[0]?.value || '')

function normalizeEblockModel(v) {
  const s = typeof v === 'string' ? v.trim() : ''
  if (s) return s
  return defaultEblockModel.value
}

function getEblockModelRecord(modelTag) {
  const normalized = normalizeEblockModel(modelTag)
  return eblockModelRaw.value.find((item) => (item.productModel || item.modelName || '').trim() === normalized) ?? null
}

function getEblockModelMetrics(modelTag) {
  const record = getEblockModelRecord(modelTag)
  return {
    maxPower: record?.ratedPower ?? undefined,
    capacity: record?.ratedCapacity ?? undefined,
  }
}

function isLiquidCooledEblockModel(modelTag) {
  return Number(getEblockModelRecord(modelTag)?.heatManagement) === 1
}

/**
 * 从 eBlock 型号记录中提取电池簇数量与型号
 * clusterCount 为空/非正数时按 1 处理（表示单簇，仅提交 eBlock 自身）
 */
function getEblockClusterInfo(modelTag) {
  const record = getEblockModelRecord(modelTag)
  const rawCount = Number(record?.clusterCount)
  const clusterCount = Number.isFinite(rawCount) && rawCount > 0 ? Math.floor(rawCount) : 1
  const batteryClusterModel = String(record?.batteryClusterModel ?? '').trim()
  return { clusterCount, batteryClusterModel }
}

/**
 * 根据 eBlock draft item 生成前端 batteryClusterList（用于回显与提交）
 * - 编辑态优先复用 existingClusters（保留 equipmentId、名称、devId 等原始信息）
 * - 新增态按 clusterCount / batteryClusterModel 自动生成 UUID / devId / 名称
 */
function buildEblockBatteryClusterList(elinkOrder, eblockOrder, modelTag, existingClusters = []) {
  const { clusterCount, batteryClusterModel } = getEblockClusterInfo(modelTag)
  if (clusterCount <= 1) return []
  const list = Array.isArray(existingClusters) ? existingClusters : []
  if (list.length) {
    return list.map((item, index) => {
      const clusterSort = index + 1
      return {
        equipmentId: item.equipmentId ?? null,
        equipmentName: item.equipmentName || item.name || `${elinkOrder}-${eblockOrder}-${clusterSort}#电池簇`,
        productModel: item.productModel || item.modelTag || batteryClusterModel,
        uuid: item.uuid || buildEblockBatteryClusterUuid(elinkOrder, eblockOrder, clusterSort),
        devId: item.devId || item.deviceId || buildEblockBatteryClusterDevId(elinkOrder, eblockOrder, clusterSort),
        sort: clusterSort,
      }
    })
  }
  return Array.from({ length: clusterCount }, (_, i) => {
    const clusterSort = i + 1
    return {
      equipmentId: null,
      equipmentName: `${elinkOrder}-${eblockOrder}-${clusterSort}#电池簇`,
      productModel: batteryClusterModel,
      uuid: buildEblockBatteryClusterUuid(elinkOrder, eblockOrder, clusterSort),
      devId: buildEblockBatteryClusterDevId(elinkOrder, eblockOrder, clusterSort),
      sort: clusterSort,
    }
  })
}

function normalizeEblockItem(eb) {
  const nestedWaterMachine = Array.isArray(eb.waterMachineList) ? eb.waterMachineList[0] ?? {} : {}
  const modelTag = normalizeEblockModel(eb.modelTag)
  const metrics = getEblockModelMetrics(modelTag)
  const maxPower = Number(eb?.maxPower)
  const capacity = Number(eb?.capacity)
  const batteryClusterList = Array.isArray(eb.batteryClusterList)
    ? eb.batteryClusterList.map((cl) => ({
      equipmentId: cl.equipmentId ?? cl.id ?? null,
      equipmentName: cl.equipmentName || cl.name || '',
      productModel: cl.productModel || cl.modelTag || '',
      uuid: cl.uuid || '',
      devId: cl.devId || cl.deviceId || '',
      sort: cl.sort ?? 0,
    }))
    : []
  return {
    key: eb.key || newEblockKey(),
    name: (eb.name ?? '').trim(),
    modelTag,
    uuid: (eb.uuid ?? '').trim(),
    deviceId: (eb.deviceId ?? '').trim(),
    maxPower: Number.isFinite(maxPower) ? maxPower : Number(metrics.maxPower) || 0,
    capacity: Number.isFinite(capacity) ? capacity : Number(metrics.capacity) || 0,
    waterMachineName: (eb.waterMachineName ?? nestedWaterMachine.waterMachineName ?? '').trim(),
    waterMachineModel: (eb.waterMachineModel ?? nestedWaterMachine.productModel ?? '').trim(),
    waterMachineUuid: (eb.waterMachineUuid ?? nestedWaterMachine.uuid ?? '').trim(),
    waterMachineDevId: (eb.waterMachineDevId ?? nestedWaterMachine.devId ?? '').trim(),
    batteryClusterList,
  }
}

function createEblockDraftItem(elinkIdx, index, modelTag = '', waterMachineModel = '') {
  const seq = Math.max(1, Number(index) || 1)
  const normalizedElinkOrder = Math.max(0, Number(elinkIdx) || 0)
  const tag = normalizeEblockModel(modelTag)
  const { maxPower, capacity } = getEblockModelMetrics(tag)
  const base = {
    key: newEblockKey(),
    name: `${normalizedElinkOrder}-${seq}#eBlock`,
    modelTag: tag,
    uuid: buildEblockUuid(normalizedElinkOrder, seq),
    deviceId: `EBLOCK_${normalizedElinkOrder}_${seq}`,
    maxPower: Number(maxPower) || 0,
    capacity: Number(capacity) || 0,
    waterMachineName: '',
    waterMachineModel: '',
    waterMachineUuid: '',
    waterMachineDevId: '',
  }
  if (isLiquidCooledEblockModel(tag) && waterMachineModel) {
    return {
      ...base,
      ...buildWaterMachineMeta(elinkIdx, seq, waterMachineModel),
    }
  }
  return base
}

function rowEblocksSource(row) {
  return Array.isArray(row?.eblocks) ? row.eblocks : []
}

function resolveEblocks(row) {
  if (Array.isArray(row.eblocks)) {
    return row.eblocks.map((eb) => normalizeEblockItem(eb))
  }
  return []
}

function eblocksForRow(row) {
  return resolveEblocks(row)
}

/**
 * 将 eBlock 列表展开为「按电池簇分行」的展示列表，用于 rowspan 合并渲染：
 * - 多簇 (batteryClusterList.length > 1)：每簇一行，首行 isFirst=true 用 rowspan 覆盖 eBlock 列
 * - 单簇或无簇：仅一行，cluster 为 batteryClusterList[0] 或 null
 * 返回结构 { key, eb, cluster, clusterIdx, clusterCount, isFirst }
 */
function eblockDisplayRowsForRow(row) {
  const list = eblocksForRow(row)
  const rows = []
  list.forEach((eb) => {
    const clusters = Array.isArray(eb.batteryClusterList) ? eb.batteryClusterList : []
    if (clusters.length > 1) {
      clusters.forEach((cl, idx) => {
        rows.push({
          key: `${eb.key}-c${idx}`,
          eb,
          cluster: cl,
          clusterIdx: idx,
          clusterCount: clusters.length,
          isFirst: idx === 0,
        })
      })
    } else {
      rows.push({
        key: eb.key,
        eb,
        cluster: clusters[0] || null,
        clusterIdx: 0,
        clusterCount: 1,
        isFirst: true,
      })
    }
  })
  return rows
}

/** 该 elink 是否存在多电池簇 eBlock，用于决定是否显示"电池簇不支持编辑"提示 */
function hasMultiClusterEblock(row) {
  return eblocksForRow(row).some((eb) => Array.isArray(eb.batteryClusterList) && eb.batteryClusterList.length > 1)
}

function clusterForRow(row) {
  return Array.isArray(row?.cluster) ? row.cluster : []
}

function eblockCountForRow(row) {
  if (Array.isArray(row.eblocks)) return row.eblocks.length
  return 0
}

/** 集中式电站（galaxy）下的电池簇数量：由 galaxy 型号带出的 clusterCount 字段决定 */
function clusterCountForRow(row) {
  const modelTag = String(row?.galaxyModelTag ?? '').trim()
  if (!modelTag) return 0
  const record = getEblockModelRecord(modelTag)
  const count = Number(record?.clusterCount)
  return Number.isFinite(count) && count > 0 ? Math.floor(count) : 0
}

function cloneEblocksForDraft(row) {
  const list = eblocksForRow(row)
  if (!list.length) {
    return {
      count: 1,
      modelTag: normalizeEblockModel(''),
    }
  }
  return {
    count: list.length,
    modelTag: normalizeEblockModel(list[0].modelTag),
  }
}

function buildEblockDraftList() {
  const count = Math.floor(Number(elinkDraft.eblockCount))
  const modelTag = normalizeEblockModel(elinkDraft.eblockModelTag)
  const elinkIdx = String(elinkDraft.badge ?? '').trim() || '0'
  const eblockWaterMachineModel = shouldShowDraftEblockWaterMachineFields.value
    ? String(elinkDraft.eblockWaterMachineModel || '').trim()
    : ''
  return Array.from({ length: count }, (_, index) => {
    const waterMachineModel = eblockWaterMachineModel
    return createEblockDraftItem(elinkIdx, index + 1, modelTag, waterMachineModel)
  })
}

function primaryEblockModelTag(row) {
  const list = eblocksForRow(row)
  if (!list.length) return normalizeEblockModel('')
  return normalizeEblockModel(list[0].modelTag)
}

function findElinkRow(elinkId) {
  return elinkRows.value.find((r) => r.id === elinkId) ?? null
}

function updateElinkEblocks(elinkId, eblocks) {
  const idx = elinkRows.value.findIndex((r) => r.id === elinkId)
  if (idx === -1) return false
  const list = [...elinkRows.value]
  list[idx] = { ...list[idx], eblocks: eblocks.map((eb) => normalizeEblockItem(eb)), cluster: [] }
  elinkRows.value = list
  return true
}

function normalizeElinkWaterMachineItem(item, elinkOrder = 0, index = 0) {
  const order = index + 1
  return {
    id: item?.id ?? item?.waterMachineId ?? null,
    parentEquipmentId: item?.parentEquipmentId ?? null,
    key: item?.key || newWaterMachineKey(),
    name: (item?.name ?? item?.waterMachineName ?? `${order}#水机`).trim(),
    productModel: (item?.productModel ?? item?.waterMachineModel ?? '').trim(),
    uuid: (item?.uuid ?? item?.waterMachineUuid ?? buildElinkWaterMachineUuid(elinkOrder, order)).trim(),
    deviceId: (item?.deviceId ?? item?.waterMachineDevId ?? item?.devId ?? `WATER_${Math.max(0, Number(elinkOrder) || 0)}_${order}`).trim(),
  }
}

async function loadWaterMachineList() {
  if (!props.stationId) return []
  try {
    const res = await getWaterMachineList({
      stationId: props.stationId,
      pageNum: 1,
      pageSize: 500,
    })
    const list = res?.rows ?? res?.data?.rows ?? res?.data ?? []
    return Array.isArray(list) ? list : []
  } catch (err) {
    console.error('获取水机列表失败:', err)
    return []
  }
}

function buildWaterMachineMapByParent(list) {
  return list.reduce((acc, item) => {
    const parentId = item?.parentEquipmentId
    if (parentId == null || parentId === '') return acc
    const key = String(parentId)
    if (!acc.has(key)) acc.set(key, [])
    acc.get(key).push(item)
    return acc
  }, new Map())
}

function buildWaterMachinePayload({
  row,
  draft,
  productModel,
  stationId,
  elinkId,
  parentEquipmentId,
}) {
  const payload = {
    stationId,
    unitId: 1,
    uuid: draft.uuid,
    devId: draft.deviceId,
    waterMachineName: draft.name,
    productModel,
    sort: Number(draft.sort) || 1,
  }

  if (parentEquipmentId != null && parentEquipmentId !== '') {
    payload.parentEquipmentId = Number(parentEquipmentId)
  } else if (elinkId != null && elinkId !== '') {
    payload.parentEquipmentId = Number(elinkId)
  } else if (row?.id != null && row?.id !== '') {
    payload.parentEquipmentId = Number(row.id)
  }

  return payload
}

function syncRowWaterMachines(row, waterMachines) {
  if (!row?.id) return
  const idx = elinkRows.value.findIndex((item) => item.id === row.id)
  if (idx === -1) return
  const elinkOrder = Math.max(0, Number(row.badge ?? row.id) || 0)
  const normalized = waterMachines.map((item, index) => normalizeElinkWaterMachineItem(item, elinkOrder, index))
  const next = [...elinkRows.value]
  next[idx] = {
    ...next[idx],
    waterMachineList: normalized,
  }
  elinkRows.value = next
}

function refreshRowWaterMachinesFromList(row, waterMachineList) {
  if (!row?.id) return
  const related = waterMachineList.filter((item) => Number(item?.parentEquipmentId) === Number(row.id))
  syncRowWaterMachines(row, related)
}

function findWaterMachineParentEquipmentId(row, waterMachine) {
  const current = Array.isArray(row?.waterMachineList)
    ? row.waterMachineList.find((item) => item.key === waterMachine?.key
      || (item.uuid === waterMachine?.uuid && item.deviceId === waterMachine?.deviceId))
    : null
  return current?.parentEquipmentId ?? waterMachine?.parentEquipmentId ?? row?.id ?? null
}

function buildWaterMachineFallbackRow(row, draft) {
  return {
    id: draft.id ?? null,
    parentEquipmentId: findWaterMachineParentEquipmentId(row, draft),
    key: draft.key,
    name: draft.name,
    productModel: draft.productModel ?? '',
    uuid: draft.uuid,
    deviceId: draft.deviceId,
  }
}

function elinkWaterMachinesForExpand(row) {
  const elinkOrder = Math.max(0, Number(row?.badge ?? row?.id) || 0)
  if (!Array.isArray(row?.waterMachineList) || !row.waterMachineList.length) {
    return []
  }
  return row.waterMachineList.map((item, index) => normalizeElinkWaterMachineItem(item, elinkOrder, index))
}

function createEmptyMeteringMeter(elinkOrder = 0, meterOrder = 1) {
  return {
    key: newMeteringMeterKey(),
    name: '',
    uuid: buildElinkMeterUuid(elinkOrder, meterOrder),
    deviceId: '',
  }
}

function meteringMeterCount(row) {
  if (Array.isArray(row.meteringMeters)) return row.meteringMeters.length
  const legacy = row.meterCount
  return legacy != null ? Number(legacy) || 0 : 0
}

/** 展开卡片内展示的计量电表列表（有明细时逐条展示） */
function meteringMetersForExpand(row) {
  if (!Array.isArray(row.meteringMeters) || !row.meteringMeters.length) return []
  return row.meteringMeters.map((m, i) => ({
    id: m.id ?? null,
    stationId: m.stationId ?? props.stationId ?? '',
    meterType: m.meterType ?? 2,
    productModel: m.productModel ?? '2',
    productRatio: m.productRatio ?? 1,
    productLocation: m.productLocation ?? '1',
    linkEquipmentUuid: m.linkEquipmentUuid ?? row.uuid ?? '',
    key: m.key || `${row.id}-mm-${i}-${m.uuid ?? ''}`,
    name: (m.name ?? '').trim(),
    uuid: (m.uuid ?? '').trim(),
    deviceId: (m.deviceId ?? '').trim(),
  }))
}

async function copyElinkClipboard(text) {
  const s = String(text ?? '').trim()
  if (!s) {
    eui.showToast('暂无可复制内容', 'info')
    return
  }
  try {
    await navigator.clipboard.writeText(s)
    eui.showToast('已复制到剪贴板', 'success')
  } catch {
    eui.showToast('复制失败，请手动选择文本复制', 'error')
  }
}

function isElinkVpnConfigured(row) {
  return Number(row?.vpnConfigured) === 1
}

function canConfigureElinkVpn(row) {
  return [0, 2].includes(Number(row?.vpnConfigured ?? 0))
}

function updateElinkVpnConfigured(row, vpnConfigured) {
  const idx = elinkRows.value.findIndex((item) => item.id === row?.id)
  if (idx === -1) return
  const list = [...elinkRows.value]
  list[idx] = { ...list[idx], vpnConfigured }
  elinkRows.value = list
}

async function handleConfigureElinkVpn(row) {
  const uuid = String(row?.uuid ?? '').trim()
  if (!uuid) {
    eui.showToast('能量链 UUID 为空，无法配置 VPN', 'error')
    return
  }

  configuringElinkUuid.value = uuid
  try {
    await configureElinkVpn(uuid)
    updateElinkVpnConfigured(row, 1)
    eui.showToast('VPN 配置成功', 'success')
  } catch (err) {
    console.error('配置能量链 VPN 失败:', err)
    eui.showToast(err?.message || 'VPN 配置失败，请重试', 'error')
  } finally {
    configuringElinkUuid.value = ''
  }
}

async function handleDownloadElinkOvpn(row) {
  const uuid = String(row?.uuid ?? '').trim()
  if (!uuid) {
    eui.showToast('能量链 UUID 为空，无法下载配置', 'error')
    return
  }

  downloadingElinkUuid.value = uuid
  try {
    const data = await downloadElinkOvpn(uuid)
    saveBlobFile(data, `${uuid}.ovpn`)
    eui.showToast('OpenVPN 配置已下载', 'success')
  } catch (err) {
    console.error('下载能量链 OpenVPN 配置失败:', err)
    eui.showToast(err?.message || '下载 OpenVPN 配置失败，请重试', 'error')
  } finally {
    downloadingElinkUuid.value = ''
  }
}

function cloneMeteringMetersForDraft(row) {
  const elinkOrder = Math.max(0, Number(row?.badge ?? row?.id) || 0)
  if (Array.isArray(row.meteringMeters)) {
    if (row.meteringMeters.length) {
      return row.meteringMeters.map((m, index) => ({
        key: m.key || newMeteringMeterKey(),
        name: m.name ?? '',
        uuid: m.uuid ?? buildElinkMeterUuid(elinkOrder, index + 1),
        deviceId: m.deviceId ?? '',
      }))
    }
    return []
  }
  const n = Math.max(1, Math.floor(Number(row.meterCount) || 1))
  return Array.from({ length: n }, (_, index) => createEmptyMeteringMeter(elinkOrder, index + 1))
}

function addElinkMeteringMeter() {
  const elinkOrder = Math.max(0, Number(elinkDraft.badge) || 0)
  const nextOrder = nextElinkMeterOrder(elinkDraft.meteringMeters)
  elinkDraft.meteringMeters.push(createEmptyMeteringMeter(elinkOrder, nextOrder))
}

function removeElinkMeteringMeter(index) {
  if (index < 0 || index >= elinkDraft.meteringMeters.length) return
  elinkDraft.meteringMeters.splice(index, 1)
}

function createEmptyDraftElinkWaterMachine(elinkOrder = 0, order = 1) {
  const normalizedOrder = Math.max(1, Number(order) || 1)
  return normalizeElinkWaterMachineItem({}, elinkOrder, normalizedOrder - 1)
}

function addDraftElinkWaterMachine() {
  const elinkOrder = Math.max(0, Number(elinkDraft.badge) || 0)
  const nextOrder = nextElinkWaterMachineOrder(elinkDraft.waterMachineList)
  const item = createEmptyDraftElinkWaterMachine(elinkOrder, nextOrder)
  item.deviceId = ''
  elinkDraft.waterMachineList.push(item)
}

function updateDraftElinkWaterMachineModel(index, value) {
  if (index < 0 || index >= elinkDraft.waterMachineList.length) return
  const elinkOrder = Math.max(0, Number(elinkDraft.badge) || 0)
  const nextList = elinkDraft.waterMachineList.map((item, itemIndex) => {
    const nextItem = itemIndex === index
      ? { ...item, productModel: typeof value === 'string' ? value.trim() : '' }
      : item
    return normalizeElinkWaterMachineItem(nextItem, elinkOrder, itemIndex)
  })
  elinkDraft.waterMachineList = nextList
}

function removeDraftElinkWaterMachine(waterMachine) {
  const elinkOrder = Math.max(0, Number(elinkDraft.badge) || 0)
  elinkDraft.waterMachineList = elinkDraft.waterMachineList
    .filter((item) => item.key !== waterMachine?.key && !(item.uuid === waterMachine?.uuid && item.deviceId === waterMachine?.deviceId))
    .map((item, index) => normalizeElinkWaterMachineItem(item, elinkOrder, index))
}

function askDeleteElinkWaterMachine(row, waterMachine) {
  deleteDialog.kind = 'elink-water-machine'
  deleteDialog.title = '删除 eLink 水机'
  deleteDialog.target = {
    ...waterMachine,
    name: waterMachine?.name ?? '',
    uuid: waterMachine?.uuid ?? '',
    deviceId: waterMachine?.deviceId ?? '',
    elinkId: row?.id ?? null,
  }
  deleteDialog.open = true
}

function openElinkMeterAdd(row) {
  meterDialog.isEdit = false
  meterDialog.elinkId = row?.id ?? null
  meterDialog.meterKey = ''
  resetMeterDraft()
  const elinkOrder = Math.max(0, Number(row?.badge ?? row?.id) || 0)
  const meterOrder = nextElinkMeterOrder(meteringMetersForExpand(row))
  meterDraft.meterName = ''
  meterDraft.uuid = buildElinkMeterUuid(elinkOrder, meterOrder)
  meterDraft.devId = ''
  meterDialog.open = true
  if (row?.id != null && !expandedElinkIds.value.includes(row.id)) {
    expandedElinkIds.value = [...expandedElinkIds.value, row.id]
  }
}

function openElinkMeterEdit(row, meter) {
  meterDialog.isEdit = true
  meterDialog.elinkId = row?.id ?? null
  meterDialog.meterKey = meter?.key || ''
  resetMeterDraft()
  Object.assign(meterDraft, {
    id: meter?.id ?? null,
    uuid: meter?.uuid ?? '',
    devId: meter?.deviceId ?? '',
    stationId: meter?.stationId ?? props.stationId ?? '',
    unitId: 1,
    meterName: meter?.name ?? '',
    meterType: meter?.meterType != null ? Number(meter?.meterType) : 2,
    productModel: meter?.productModel ?? '2',
    productRatio: meter?.productRatio ?? 1,
    productLocation: meter?.productLocation != null ? String(meter?.productLocation) : '1',
    linkEquipmentUuid: meter?.linkEquipmentUuid ?? row?.uuid ?? '',
  })
  meterDialog.open = true
  if (row?.id != null && !expandedElinkIds.value.includes(row.id)) {
    expandedElinkIds.value = [...expandedElinkIds.value, row.id]
  }
}

function askDeleteElinkMeter(row, meter) {
  deleteDialog.kind = 'elink-meter'
  deleteDialog.title = '删除计量电表'
  deleteDialog.target = {
    ...meter,
    meterName: meter?.name ?? '',
    devId: meter?.deviceId ?? '',
    elinkId: row?.id ?? null,
  }
  deleteDialog.open = true
}

const elinkDialog = reactive({
  open: false,
  isEdit: false,
  submitting: false,
})

const elinkWaterMachineDialog = reactive({
  open: false,
  isEdit: false,
  submitting: false,
})

const elinkWaterMachineDraft = reactive({
  elinkId: null,
  key: '',
  id: null,
  parentEquipmentId: null,
  name: '',
  productModel: '',
  uuid: '',
  deviceId: '',
  sort: 1,
})

const elinkDraft = reactive({
  id: null,
  name: '',
  uuid: '',
  emqxClientId: '',
  modelTag: 'eLink3000',
  deviceNetworkId: '',
  measuringDistance: '',
  eLinkName: '',
  eblockCount: 1,
  eblockModelTag: '',
  eblockWaterMachineModel: '',
  badge: '',
  meteringMeters: [],
  waterMachineList: [],
})

// 集中式 Galaxy 展示（Galaxy 名称/型号/UUID + 电池簇/PCS 数量与型号）
// batteryClusterList 保存实际的电池簇数据（用于回显与提交，保留 equipmentId 等原始信息）
const galaxyDraft = reactive({
  name: '',
  modelTag: '',
  uuid: '',
  clusterCount: '',
  pcsCount: '',
  batteryClusterModel: '',
  pcsModel: '',
  batteryClusterList: [],
})

function resetGalaxyDraft() {
  galaxyDraft.name = ''
  galaxyDraft.modelTag = ''
  galaxyDraft.uuid = ''
  galaxyDraft.clusterCount = ''
  galaxyDraft.pcsCount = ''
  galaxyDraft.batteryClusterModel = ''
  galaxyDraft.pcsModel = ''
  galaxyDraft.batteryClusterList = []
}

function clearGalaxyMetrics() {
  galaxyDraft.clusterCount = ''
  galaxyDraft.pcsCount = ''
  galaxyDraft.batteryClusterModel = ''
  galaxyDraft.pcsModel = ''
}

/**
 * Galaxy 型号变化后拉取详情，回填电池簇数量/PCS 数量/电池簇型号/PCS 型号
 * 接口：GET /cloud/device/v3/eblock/model/list?productModel=xxx
 */
async function fetchGalaxyModelDetail(productModel) {
  const model = String(productModel ?? '').trim()
  if (!model) {
    clearGalaxyMetrics()
    return
  }
  try {
    const res = await listEBlockModel({ pageNum: 1, pageSize: 10, productModel: model })
    const list = res?.rows ?? res?.data?.rows ?? res?.data ?? []
    const row = Array.isArray(list) ? list[0] : null
    if (!row) {
      clearGalaxyMetrics()
      return
    }
    galaxyDraft.pcsCount = row.pcsCount ?? ''
    galaxyDraft.pcsModel = row.pcsModel ?? ''
    const existingClusters = Array.isArray(galaxyDraft.batteryClusterList) ? galaxyDraft.batteryClusterList : []
    if (existingClusters.length) {
      galaxyDraft.clusterCount = existingClusters.length
      galaxyDraft.batteryClusterModel = existingClusters[0]?.productModel || row.batteryClusterModel || ''
    } else {
      galaxyDraft.clusterCount = row.clusterCount ?? ''
      galaxyDraft.batteryClusterModel = row.batteryClusterModel ?? ''
    }
  } catch (err) {
    console.error('拉取 Galaxy 型号详情失败:', err)
    clearGalaxyMetrics()
  }
}

watch(() => galaxyDraft.modelTag, (val) => {
  if (!elinkDialog.open || isDistributedStation.value) return
  fetchGalaxyModelDetail(val)
  syncGalaxyElinkName()
})

function syncGalaxyElinkName() {
  if (!elinkDialog.open || elinkDialog.isEdit || isDistributedStation.value) return
  const modelTag = String(galaxyDraft.modelTag ?? '').trim()
  if (!modelTag) return
  const badge = String(elinkDraft.badge ?? '').trim() || '1'
  elinkDraft.eLinkName = `${badge}#eBlock-${modelTag}`
}

const eblockDialog = reactive({
  open: false,
  isEdit: false,
})

const eblockDraft = reactive({
  elinkId: null,
  key: '',
  name: '',
  modelTag: '',
  uuid: '',
  deviceId: '',
  waterMachineName: '',
  waterMachineModel: '',
  waterMachineUuid: '',
  waterMachineDevId: '',
})

watch(
  () => filteredEblockModelOptions.value,
  (options) => {
    const firstModel = options[0]?.value || ''
    if (!options.some((item) => item.value === elinkDraft.eblockModelTag)) {
      elinkDraft.eblockModelTag = firstModel
    }
    if (!options.some((item) => item.value === eblockDraft.modelTag)) {
      eblockDraft.modelTag = firstModel
    }
  },
)

const shouldShowDraftEblockWaterMachineFields = computed(() => isLiquidCooledEblockModel(elinkDraft.eblockModelTag))
const shouldShowEblockWaterMachineFields = computed(() => isLiquidCooledEblockModel(eblockDraft.modelTag))

function syncDraftEblockWaterMachineModel() {
  if (!shouldShowDraftEblockWaterMachineFields.value) {
    elinkDraft.eblockWaterMachineModel = ''
  }
}

function clearEblockWaterMachineDraft() {
  eblockDraft.waterMachineName = ''
  eblockDraft.waterMachineModel = ''
  eblockDraft.waterMachineUuid = ''
  eblockDraft.waterMachineDevId = ''
}

function syncEblockWaterMachineDraft() {
  if (!shouldShowEblockWaterMachineFields.value) {
    clearEblockWaterMachineDraft()
    return
  }
  const elinkRow = findElinkRow(eblockDraft.elinkId)
  const elinkIdx = elinkRow?.badge ?? elinkRow?.id ?? '0'
  const seq = inferEblockSeq(eblockDraft.name, 1)
  const meta = buildWaterMachineMeta(elinkIdx, seq, eblockDraft.waterMachineModel)
  eblockDraft.waterMachineUuid = meta.waterMachineUuid
}

function syncEblockDraftDerivedFields() {
  const elinkRow = findElinkRow(eblockDraft.elinkId)
  const elinkOrder = Math.max(0, Number(elinkRow?.badge ?? elinkRow?.id) || 0)
  const seq = inferEblockSeq(eblockDraft.name, 1)
  eblockDraft.uuid = buildEblockUuid(elinkOrder, seq)
}

watch(() => shouldShowDraftEblockWaterMachineFields.value, () => {
  syncDraftEblockWaterMachineModel()
  if (!elinkDialog.open || elinkDialog.isEdit) return
  syncElinkDraftDerivedFields()
})

watch(() => shouldShowEblockWaterMachineFields.value, () => {
  syncEblockDraftDerivedFields()
  syncEblockWaterMachineDraft()
})

watch(() => eblockDraft.name, () => {
  syncEblockDraftDerivedFields()
  if (shouldShowEblockWaterMachineFields.value) syncEblockWaterMachineDraft()
})

watch(() => eblockDraft.elinkId, () => {
  syncEblockDraftDerivedFields()
  if (shouldShowEblockWaterMachineFields.value) syncEblockWaterMachineDraft()
})

watch(() => [elinkDraft.badge, elinkDraft.eblockCount, elinkDraft.eblockModelTag, elinkDraft.eblockWaterMachineModel], () => {
  if (!elinkDialog.open || elinkDialog.isEdit) return
  syncElinkDraftDerivedFields()
  syncGalaxyElinkName()
})

const meterDialog = reactive({
  open: false,
  isEdit: false,
  elinkId: null,
  meterKey: '',
})

function resetElinkWaterMachineDraft() {
  Object.assign(elinkWaterMachineDraft, {
    elinkId: null,
    key: '',
    id: null,
    parentEquipmentId: null,
    name: '',
    productModel: '',
    uuid: '',
    deviceId: '',
    sort: 1,
  })
}

function closeElinkWaterMachineDialog() {
  elinkWaterMachineDialog.open = false
  elinkWaterMachineDialog.submitting = false
}

function openElinkWaterMachineAdd(row = null) {
  if (!row) {
    addDraftElinkWaterMachine()
    return
  }
  resetElinkWaterMachineDraft()
  elinkWaterMachineDialog.isEdit = false
  const elinkOrder = Math.max(0, Number(row.badge ?? row.id) || 0)
  const currentList = elinkWaterMachinesForExpand(row)
  const nextOrder = nextElinkWaterMachineOrder(currentList)
  const nextItem = createEmptyDraftElinkWaterMachine(elinkOrder, nextOrder)
  Object.assign(elinkWaterMachineDraft, {
    ...nextItem,
    deviceId: '',
    elinkId: row.id ?? null,
    sort: nextOrder,
    parentEquipmentId: row.id ?? null,
  })
  loadConditionerModels()
  elinkWaterMachineDialog.open = true
  if (row.id != null && !expandedElinkIds.value.includes(row.id)) {
    expandedElinkIds.value = [...expandedElinkIds.value, row.id]
  }
}

function openElinkWaterMachineEdit(row, waterMachine) {
  if (!row) return
  resetElinkWaterMachineDraft()
  elinkWaterMachineDialog.isEdit = true
  const elinkOrder = Math.max(0, Number(row.badge ?? row.id) || 0)
  const currentList = elinkWaterMachinesForExpand(row)
  const index = currentList.findIndex((item) => item.key === waterMachine?.key
    || item.id === waterMachine?.id
    || (item.uuid === waterMachine?.uuid && item.deviceId === waterMachine?.deviceId))
  const normalized = normalizeElinkWaterMachineItem(
    waterMachine,
    elinkOrder,
    index === -1 ? currentList.length : index,
  )
  Object.assign(elinkWaterMachineDraft, {
    ...normalized,
    elinkId: row.id ?? null,
    sort: (index === -1 ? currentList.length : index) + 1,
    parentEquipmentId: normalized.parentEquipmentId ?? row.id ?? null,
  })
  loadConditionerModels()
  elinkWaterMachineDialog.open = true
  if (row.id != null && !expandedElinkIds.value.includes(row.id)) {
    expandedElinkIds.value = [...expandedElinkIds.value, row.id]
  }
}

async function submitElinkWaterMachine() {
  const row = findElinkRow(elinkWaterMachineDraft.elinkId)
  if (!row) {
    eui.showToast('未找到所属能量链', 'error')
    return
  }

  const name = String(elinkWaterMachineDraft.name || '').trim()
  const uuid = String(elinkWaterMachineDraft.uuid || '').trim()
  const deviceId = String(elinkWaterMachineDraft.deviceId || '').trim()
  const productModel = String(elinkWaterMachineDraft.productModel || '').trim()

  if (!name || !uuid || !deviceId || !productModel) {
    eui.showToast('请填写水机名称、产品型号、UUID 与设备 devID', 'error')
    return
  }

  const draftItem = normalizeElinkWaterMachineItem({
    id: elinkWaterMachineDraft.id,
    parentEquipmentId: elinkWaterMachineDraft.parentEquipmentId ?? row.id ?? null,
    key: elinkWaterMachineDraft.key,
    name,
    productModel,
    uuid,
    deviceId,
  }, Math.max(0, Number(row.badge ?? row.id) || 0), Math.max(0, Number(elinkWaterMachineDraft.sort) - 1 || 0))

  const payload = buildWaterMachinePayload({
    row,
    draft: draftItem,
    productModel,
    stationId: Number(props.stationId),
    elinkId: row.id,
    parentEquipmentId: draftItem.parentEquipmentId ?? row.id ?? null,
  })

  elinkWaterMachineDialog.submitting = true
  try {
    if (elinkWaterMachineDialog.isEdit && elinkWaterMachineDraft.id != null) {
      await updateWaterMachine({ ...payload, id: elinkWaterMachineDraft.id })
    } else {
      await addWaterMachine(payload)
    }
    const latestWaterMachines = await loadWaterMachineList()
    refreshRowWaterMachinesFromList(row, latestWaterMachines)
    closeElinkWaterMachineDialog()
    eui.showToast(elinkWaterMachineDialog.isEdit ? '已保存 eLink 水机' : '已新增 eLink 水机', 'success')
  } catch (err) {
    console.error(`${elinkWaterMachineDialog.isEdit ? '保存' : '新增'} eLink 水机失败:`, err)
    if (elinkWaterMachineDialog.isEdit) {
      const fallbackList = elinkWaterMachinesForExpand(row).map((item, index) => normalizeElinkWaterMachineItem(item, Math.max(0, Number(row.badge ?? row.id) || 0), index))
      const targetIndex = fallbackList.findIndex((item) => item.id === elinkWaterMachineDraft.id
        || item.key === elinkWaterMachineDraft.key
        || (item.uuid === elinkWaterMachineDraft.uuid && item.deviceId === elinkWaterMachineDraft.deviceId))
      if (targetIndex !== -1) {
        fallbackList.splice(targetIndex, 1, buildWaterMachineFallbackRow(row, draftItem))
        syncRowWaterMachines(row, fallbackList)
      }
    }
    eui.showToast(err?.message || `${elinkWaterMachineDialog.isEdit ? '保存' : '新增'} eLink 水机失败`, 'error')
  } finally {
    elinkWaterMachineDialog.submitting = false
  }
}

function createEmptyMeterDraft() {
  return {
    id: null,
    uuid: '',
    devId: '',
    stationId: '',
    unitId: 1,
    meterName: '',
    meterType: '',
    commInterface: '',
    factoryName: '',
    productModel: '',
    productRatio: '',
    productLocation: '0',
    imageUrl: '',
    productDescription: '',
    measuringEquipIdStr: '',
    assetCode: '',
    assetProperty: '',
    assetUnit: '',
    parentMeterUuid: '',
    linkEquipmentUuid: '',
    sort: 0,
  }
}

function nextMeterOrderByType(meterType) {
  const config = meterUuidConfigMap[Number(meterType)]
  if (!config) return 0
  const orders = meterRows.value
    .map((row) => {
      if (Number(row?.meterType) !== Number(meterType)) return NaN
      const match = String(row?.uuid ?? '').trim().match(config.pattern)
      return match ? Number(match[1]) : NaN
    })
    .filter((n) => !Number.isNaN(n))
  return (orders.length ? Math.max(...orders) : 0) + 1
}

function syncMeterUuid() {
  if (meterDialog.elinkId != null || meterDialog.isEdit) return
  const meterType = Number(meterDraft.meterType)
  meterDraft.uuid = buildMeterUuid(meterType, nextMeterOrderByType(meterType))
}

function beforeMeterImageUpload(file) {
  const isImage = file?.type?.startsWith?.('image/')
  if (!isImage) {
    eui.showToast('请上传图片文件', 'error')
    return false
  }
  const isLt5M = file.size / 1024 / 1024 < 5
  if (!isLt5M) {
    eui.showToast('图片大小不能超过 5MB', 'error')
    return false
  }
  return true
}

async function requestMeterImageUpload(options) {
  const rawFile = options?.file?.raw ?? options?.file
  if (!(rawFile instanceof File)) {
    eui.showToast('未获取到有效图片文件', 'error')
    options.onError?.(new Error('invalid upload file'))
    return
  }

  try {
    const res = await uploadFile(rawFile)
    const url = normalizeUploadedFileUrl(res)
    if (!url) {
      eui.showToast('上传成功但未获取到图片地址', 'error')
      options.onError?.(new Error('missing image url'))
      return
    }
    meterDraft.imageUrl = url
    options.onSuccess?.(res, rawFile)
  } catch (err) {
    eui.showToast('图片上传失败，请重试', 'error')
    options.onError?.(err)
  }
}

function clearMeterImage() {
  meterDraft.imageUrl = ''
}

const meterDraft = reactive(createEmptyMeterDraft())
const meterSubmitting = ref(false)

watch(() => meterDraft.meterType, () => {
  syncMeterUuid()
})

watch(() => props.stationId, () => {
  if (!meterDialog.isEdit && meterDialog.elinkId == null) syncMeterUuid()
})

watch(() => [meterDraft.productModel, meterModelRaw.value.length], ([productModel]) => {
  if (meterDialog.isEdit || meterDialog.elinkId != null || !productModel) return
  const matched = meterModelRaw.value.find((m) => (m.productModel || m.modelName) === productModel)
  if (!matched) return
  meterDraft.factoryName = matched.factoryName ?? ''
  if (matched.productRatio != null) {
    meterDraft.productRatio = Number(matched.productRatio) || ''
  }
})

const transformerDialog = reactive({
  open: false,
  isEdit: false,
})

function createEmptyTransformerDraft() {
  return {
    id: null,
    stationId: '',
    uuid: '',
    devId: '',
    unitId: 1,
    transformerName: '',
    manufactorName: '',
    productModel: '',
    serialNumber: '',
    manufactureTime: '',
    ratedCapacity: '',
    voltageLevel: '',
    inputVoltage: '',
    outputVoltage: '',
    medium: '',
    coreStructure: '',
    sort: 0,
  }
}

function nextTransformerOrder() {
  const orders = transformerRows.value
    .map((row) => {
      const match = String(row?.uuid ?? '').trim().match(/^S\d+_TFM(\d+)$/)
      return match ? Number(match[1]) : NaN
    })
    .filter((n) => !Number.isNaN(n))
  return (orders.length ? Math.max(...orders) : 0) + 1
}

const transformerManufactureDateValue = computed(() => transformerDraft.manufactureTime || null)

function handleTransformerManufactureDateChange(value) {
  const pickedValue = Array.isArray(value) ? value[0] : value
  if (!pickedValue) {
    transformerDraft.manufactureTime = ''
    return
  }
  const d = dayjs(pickedValue)
  transformerDraft.manufactureTime = d.isValid() ? d.format('YYYY-MM-DD') : ''
}

const transformerDraft = reactive(createEmptyTransformerDraft())
const transformerSubmitting = ref(false)

const conditionerModelOptions = ref([])
const conditionerModelLoading = ref(false)
const conditionerModelRaw = ref([])

const pvDialog = reactive({ open: false, isEdit: false })

function createEmptyPvDraft() {
  return {
    id: null,
    uuid: '',
    devId: '',
    photovoltaicName: '',
    factoryName: '',
    productModel: '',
    parentEquipmentId: '',
    maxDcVoltage: '',
    maxDcPower: '',
    ratedOutputPower: '',
    maxOutputPower: '',
    maxAcOutputCurrent: '',
    maxInputCurrent: '',
    ratedGridVoltage: '',
    ratedGridFrequency: '',
    sort: 0,
  }
}

function nextPvOrder() {
  const orders = pvRows.value
    .map((row) => {
      const match = String(row?.uuid ?? '').trim().match(/^S\d+_PVD(\d+)$/)
      return match ? Number(match[1]) : NaN
    })
    .filter((n) => !Number.isNaN(n))
  return (orders.length ? Math.max(...orders) : 0) + 1
}

const pvDraft = reactive(createEmptyPvDraft())
const pvSubmitting = ref(false)
const pvModelOptions = ref([])
const pvModelLoading = ref(false)
const pvModelRaw = ref([])

const pileDialog = reactive({ open: false, isEdit: false })

function createEmptyPileDraft() {
  return {
    id: null,
    uuid: '',
    devId: '',
    chargePileName: '',
    factoryName: '',
    productModel: '',
    parentEquipmentId: '',
    inputVoltage: '',
    outputVoltage: '',
    ratedOutputPower: '',
    fullLoadPower: '',
    chargeInterface: '',
    chargeMode: '',
    sort: 0,
  }
}

function nextChargePileOrder() {
  const orders = pileRows.value
    .map((row) => {
      const match = String(row?.uuid ?? '').trim().match(/^S\d+_CP(\d+)$/)
      return match ? Number(match[1]) : NaN
    })
    .filter((n) => !Number.isNaN(n))
  return (orders.length ? Math.max(...orders) : 0) + 1
}

const pileDraft = reactive(createEmptyPileDraft())
const pileSubmitting = ref(false)
const pileModelOptions = ref([])
const pileModelLoading = ref(false)
const pileModelRaw = ref([])

const pileParentEquipmentOptions = computed(() => elinkRows.value
  .map((row) => {
    const equipmentId = row?.id
    if (equipmentId == null || equipmentId === '') return null
    const name = String(row?.eLinkName || '').trim()
    const equipmentIdText = String(equipmentId)
    return {
      title: name ? `${name}（${equipmentIdText}）` : equipmentIdText,
      value: equipmentIdText,
    }
  })
  .filter(Boolean))

async function loadPileModels() {
  if (pileModelOptions.value.length || pileModelLoading.value) return
  pileModelLoading.value = true
  try {
    const res = await listChargePileModel({ pageNum: 1, pageSize: 200 })
    const list = res?.rows ?? res?.data?.rows ?? res?.data ?? []
    pileModelRaw.value = Array.isArray(list) ? list : []
    pileModelOptions.value = pileModelRaw.value
      .map((m) => ({
        title: m.productModel || m.modelName || '',
        value: m.productModel || m.modelName || '',
        raw: m,
      }))
      .filter((o) => o.value)
  } catch (err) {
    console.error('拉取充电桩型号失败:', err)
  } finally {
    pileModelLoading.value = false
  }
}

function onPileModelChange(val) {
  const matched = pileModelRaw.value.find((m) => (m.productModel || m.modelName) === val)
  if (!matched) {
    clearPileDerivedFields()
    return
  }
  pileDraft.factoryName = matched.factoryName ?? ''
  pileDraft.inputVoltage = matched.inputVoltage != null ? String(matched.inputVoltage) : ''
  pileDraft.outputVoltage = matched.outputVoltage != null ? String(matched.outputVoltage) : ''
  pileDraft.ratedOutputPower = matched.ratedOutputPower != null ? String(matched.ratedOutputPower) : ''
  pileDraft.fullLoadPower = matched.fullLoadPower != null ? String(matched.fullLoadPower) : ''
  pileDraft.chargeInterface = matched.chargeInterface ?? ''
  pileDraft.chargeMode = matched.chargeMode != null && matched.chargeMode !== '' ? Number(matched.chargeMode) || '' : ''
}

function clearPileDerivedFields() {
  pileDraft.factoryName = ''
  pileDraft.inputVoltage = ''
  pileDraft.outputVoltage = ''
  pileDraft.ratedOutputPower = ''
  pileDraft.fullLoadPower = ''
  pileDraft.chargeInterface = ''
  pileDraft.chargeMode = ''
}

const deleteDialog = reactive({
  open: false,
  kind: '',
  title: '',
  target: null,
})

function nextElinkId() {
  return nextOrderFromList(elinkRows.value, (row) => inferElinkOrder(row))
}

function resetElinkDraft() {
  elinkDraft.id = null
  elinkDraft.name = ''
  elinkDraft.eLinkName = ''
  elinkDraft.uuid = ''
  elinkDraft.emqxClientId = ''
  elinkDraft.modelTag = 'eLink3000'
  elinkDraft.deviceNetworkId = ''
  elinkDraft.measuringDistance = ''
  elinkDraft.eblockCount = 1
  elinkDraft.eblockModelTag = normalizeEblockModel('')
  elinkDraft.eblockWaterMachineModel = ''
  elinkDraft.badge = ''
  elinkDraft.meteringMeters = []
  elinkDraft.waterMachineList = []
}

function syncElinkDraftDerivedFields() {
  const elinkOrder = Math.max(0, Number(elinkDraft.badge) || 0)
  const meterCount = Math.max(1, elinkDraft.meteringMeters.length || 0)
  elinkDraft.uuid = buildElinkUuid(elinkOrder)
  elinkDraft.meteringMeters = Array.from({ length: meterCount }, (_, index) => {
    const prev = elinkDraft.meteringMeters[index] ?? {}
    return {
      key: prev.key || newMeteringMeterKey(),
      name: prev.name ?? '',
      uuid: buildElinkMeterUuid(elinkOrder, index + 1),
      deviceId: prev.deviceId ?? '',
    }
  })
  elinkDraft.waterMachineList = elinkDraft.waterMachineList.map((item, index) => (
    normalizeElinkWaterMachineItem(item, elinkOrder, index)
  ))
}

function openElinkAdd() {
  resetElinkDraft()
  resetGalaxyDraft()
  elinkDialog.isEdit = false
  const nid = nextElinkId()
  elinkDraft.name = `${nid}#储能方阵`
  elinkDraft.eLinkName = isDistributedStation.value ? `${nid}#能量链` : ''
  elinkDraft.badge = String(nid)
  syncElinkDraftDerivedFields()
  // 集中式：自动生成 Galaxy UUID（与 elink 序号对齐）
  if (!isDistributedStation.value) {
    galaxyDraft.uuid = buildGalaxyUuid(nid)
  }
  loadEblockModels()
  loadConditionerModels()
  elinkDialog.open = true
}

function openElinkEdit(row) {
  elinkDialog.isEdit = true
  resetGalaxyDraft()
  // 集中式：回填 Galaxy 名称/型号/UUID + 已有电池簇（电池簇不可编辑，仅用于展示/回传）
  if (!isDistributedStation.value) {
    galaxyDraft.name = row.galaxyName ?? ''
    galaxyDraft.batteryClusterList = Array.isArray(row.galaxyBatteryClusterList)
      ? row.galaxyBatteryClusterList.map((c) => ({ ...c }))
      : []
    // modelTag 变化会触发 fetchGalaxyModelDetail；此时 batteryClusterList 已就绪，
    // fetchGalaxyModelDetail 内部会以 batteryClusterList 为准，不会用型号默认值覆盖
    galaxyDraft.modelTag = row.galaxyModelTag ?? ''
    galaxyDraft.uuid = row.galaxyUuid ?? ''
  }
  elinkDraft.id = row.id
  elinkDraft.name = row.name
  elinkDraft.eLinkName = row.eLinkName ?? ''
  elinkDraft.uuid = row.uuid ?? ''
  elinkDraft.emqxClientId = row.emqxClientId ?? ''
  elinkDraft.modelTag = row.modelTag || 'eLink3000'
  elinkDraft.deviceNetworkId = row.deviceNetworkId ?? row.devId ?? ''
  elinkDraft.measuringDistance = row.measuringDistance ?? ''
  elinkDraft.badge = row.badge != null ? String(row.badge) : String(row.id ?? '')
  const eblockDraft = cloneEblocksForDraft(row)
  elinkDraft.eblockCount = eblockDraft.count
  elinkDraft.eblockModelTag = eblockDraft.modelTag
  elinkDraft.eblockWaterMachineModel = row?.eblocks?.[0]?.waterMachineModel ?? ''
  elinkDraft.meteringMeters = cloneMeteringMetersForDraft(row)
  const elinkOrder = Math.max(0, Number(elinkDraft.badge) || 0)
  const rowWaterMachines = Array.isArray(row?.waterMachineList) ? row.waterMachineList : []
  elinkDraft.waterMachineList = rowWaterMachines.map((item, index) => normalizeElinkWaterMachineItem(item, elinkOrder, index))
  syncDraftEblockWaterMachineModel()
  syncElinkDraftDerivedFields()
  loadEblockModels()
  loadConditionerModels()
  elinkDialog.open = true
}

function closeElinkDialog() {
  elinkDialog.open = false
  resetGalaxyDraft()
}

function buildElinkPayload() {
  const trim = (v) => (typeof v === 'string' ? v.trim() : v)
  const eLinkName = trim(elinkDraft.eLinkName)
  const uuid = trim(elinkDraft.uuid)
  const badgeNum = Math.floor(Number(elinkDraft.badge))
  const badge = Number.isNaN(badgeNum) ? 0 : badgeNum
  const modelTag = trim(elinkDraft.modelTag) || 'eLink3000'
  const deviceNetworkId = trim(elinkDraft.deviceNetworkId)
  const measuringDistance = trim(elinkDraft.measuringDistance)
  const stationId = Number(props.stationId)
  const unitId = Number(props.stationId)

  const normalizedWaterMachineList = elinkDraft.waterMachineList
    .map((item, index) => normalizeElinkWaterMachineItem(item, badge, index))
    .filter((item) => trim(item.productModel) || trim(item.uuid) || trim(item.deviceId) || trim(item.name))

  const generatedEblocks = buildEblockDraftList()
  const eblockList = generatedEblocks.map((eb, index) => {
    const maxPower = Number(eb.maxPower) || 0
    const capacity = Number(eb.capacity) || 0
    const waterMachineList = trim(eb.waterMachineModel) && trim(eb.waterMachineUuid) && trim(eb.waterMachineDevId)
      ? [{
        waterMachineName: trim(eb.waterMachineName),
        productModel: trim(eb.waterMachineModel),
        uuid: trim(eb.waterMachineUuid),
        devId: trim(eb.waterMachineDevId),
        stationId,
        unitId,
        sort: 1,
      }]
      : []

    const eblockOrder = index + 1
    // 多电池簇：按 clusterCount 生成 batteryClusterList（clusterCount<=1 时返回空数组）
    const clusters = buildEblockBatteryClusterList(badge, eblockOrder, eb.modelTag, eb.batteryClusterList)
    const batteryClusterList = clusters.map((cl) => ({
      equipmentName: cl.equipmentName,
      equipmentTypeId: 5,
      stationId,
      unitId,
      productModel: cl.productModel,
      uuid: cl.uuid,
      devId: cl.devId,
      sort: cl.sort,
    }))

    const payload = {
      equipmentName: trim(eb.name),
      equipmentTypeId: 3,
      stationId,
      unitId,
      productModel: normalizeEblockModel(eb.modelTag),
      maxPower,
      capacity,
      uuid: trim(eb.uuid),
      devId: trim(eb.deviceId),
      sort: eblockOrder,
      waterMachineList,
    }
    if (batteryClusterList.length) payload.batteryClusterList = batteryClusterList
    return payload
  })

  const meterList = elinkDraft.meteringMeters.map((m, index) => ({
    meterName: trim(m.name),
    meterType: 2,
    stationId,
    unitId,
    productModel: '',
    productRatio: 1,
    productLocation: '1',
    uuid: trim(m.uuid),
    devId: trim(m.deviceId),
    sort: index + 1,
    linkEquipmentUuid: uuid || undefined,
  }))

  const payload = {
    equipmentName: eLinkName,
    equipmentTypeId: 2,
    stationId,
    unitId,
    uuid,
    productModel: modelTag,
    sort: badge,
    eblockList,
    meterList,
    waterMachineList: normalizedWaterMachineList.map((item, index) => ({
      waterMachineName: trim(item.name),
      productModel: trim(item.productModel),
      uuid: trim(item.uuid),
      devId: trim(item.deviceId),
      stationId,
      unitId,
      sort: index + 1,
    })),
  }

  if (deviceNetworkId) payload.devId = deviceNetworkId
  if (measuringDistance) payload.measuringDistance = measuringDistance

  if (elinkDraft.id) {
    payload.equipmentId = elinkDraft.id
  }

  return payload
}

/**
 * 集中式电站保存包装：Galaxy 作为设备树根节点，把 elink payload 挂到 elinkList 下
 * 后端接口：POST /device/v3/galaxy（新增）；PUT /device/v3/galaxy（编辑）
 * equipmentTypeId=4；uuid 存在→真实 Galaxy 并存储；uuid 为空→虚拟根不存储
 * 电池簇作为 batteryClusterList 数组挂在能量链（elinkList[0]）下（equipmentTypeId=5）：
 *   - 编辑：优先复用回显数据（保留 equipmentId 等原始信息，电池簇不允许在前端修改）
 *   - 新增：根据电池簇数量与型号自动生成 UUID / devId / 名称
 */
function buildGalaxyPayload(elinkPayload, galaxyEquipmentId = null) {
  const trim = (v) => (typeof v === 'string' ? v.trim() : v)
  const stationId = Number(props.stationId)
  const unitId = Number(props.stationId)
  const badgeNum = Math.floor(Number(elinkDraft.badge))
  const sort = Number.isNaN(badgeNum) ? 0 : badgeNum
  const stationIdStr = String(props.stationId ?? '').trim()

  const clusterProductModel = trim(galaxyDraft.batteryClusterModel) || ''
  const existingClusters = Array.isArray(galaxyDraft.batteryClusterList) ? galaxyDraft.batteryClusterList : []

  let batteryClusterList
  if (existingClusters.length) {
    // 编辑态：复用后端回显数据（保留 equipmentId、uuid、devId），电池簇不允许在前端修改
    batteryClusterList = existingClusters.map((item, index) => {
      const clusterSort = index + 1
      const clusterPayload = {
        equipmentName: item.equipmentName || `${sort}-${clusterSort}#电池簇`,
        equipmentTypeId: 5,
        stationId,
        unitId,
        productModel: item.productModel || clusterProductModel,
        uuid: item.uuid || (stationIdStr ? `S${stationIdStr}_GLX${sort}_BC${clusterSort}` : ''),
        devId: item.devId || `CLUSTER_${sort}_${clusterSort}`,
        sort: clusterSort,
      }
      if (item.equipmentId != null) clusterPayload.equipmentId = item.equipmentId
      if (item.maxPower != null) clusterPayload.maxPower = item.maxPower
      if (item.capacity != null) clusterPayload.capacity = item.capacity
      return clusterPayload
    })
  } else {
    // 新增态：按 galaxy 型号带出的电池簇数量/型号批量生成
    const count = Math.max(0, Math.floor(Number(galaxyDraft.clusterCount) || 0))
    batteryClusterList = Array.from({ length: count }, (_, i) => ({
      equipmentName: `${sort}-${i + 1}#电池簇`,
      equipmentTypeId: 5,
      stationId,
      unitId,
      productModel: clusterProductModel,
      uuid: stationIdStr ? `S${stationIdStr}_GLX${sort}_BC${i + 1}` : '',
      devId: `CLUSTER_${sort}_${i + 1}`,
      sort: i + 1,
    }))
  }

  const elinkWithClusters = { ...elinkPayload, batteryClusterList }

  const payload = {
    equipmentName: trim(galaxyDraft.name),
    equipmentTypeId: 4,
    stationId,
    unitId,
    uuid: trim(galaxyDraft.uuid),
    productModel: trim(galaxyDraft.modelTag),
    sort,
    elinkList: [elinkWithClusters],
  }
  if (galaxyEquipmentId != null && galaxyEquipmentId !== '') {
    payload.equipmentId = galaxyEquipmentId
  }
  return payload
}

async function submitElink() {
  if (!props.stationId) {
    eui.showToast('请先完成基本信息以获取电站ID', 'error')
    return
  }

  // 集中式：Galaxy 名称与型号必填
  if (!isDistributedStation.value) {
    if (!String(galaxyDraft.name || '').trim()) {
      eui.showToast('请填写 Galaxy 名称', 'error')
      return
    }
    if (!String(galaxyDraft.modelTag || '').trim()) {
      eui.showToast('请选择 Galaxy 型号', 'error')
      return
    }
  }

  const eLinkName = elinkDraft.eLinkName?.trim()
  if (!eLinkName) {
    eui.showToast('请填写能量链名称', 'error')
    return
  }

  const uuid = elinkDraft.uuid?.trim()
  if (!uuid) {
    eui.showToast('请填写能量链 UUID', 'error')
    return
  }

  const dupUuid = elinkRows.value.some((r) => r.uuid === uuid && r.id !== elinkDraft.id)
  if (dupUuid) {
    eui.showToast('能量链 UUID 已存在', 'error')
    return
  }

  const bStr = String(elinkDraft.badge ?? '').trim()
  if (!bStr) {
    eui.showToast('请填写序号', 'error')
    return
  }

  const badgeNum = Math.floor(Number(bStr))
  if (Number.isNaN(badgeNum)) {
    eui.showToast('序号需为有效数字', 'error')
    return
  }

  const eblockCount = Math.floor(Number(elinkDraft.eblockCount))
  if (isDistributedStation.value) {
    if (!Number.isInteger(eblockCount) || eblockCount < 1) {
      eui.showToast('请填写大于 0 的 eBlock 数量', 'error')
      return
    }
  }

  const eblockModelTag = normalizeEblockModel(elinkDraft.eblockModelTag)
  if (isDistributedStation.value && !eblockModelTag) {
    eui.showToast('请选择 eBlock 型号', 'error')
    return
  }

  if (isDistributedStation.value && shouldShowDraftEblockWaterMachineFields.value && !String(elinkDraft.eblockWaterMachineModel || '').trim()) {
    eui.showToast('液冷 eBlock 请选择水机产品型号', 'error')
    return
  }

  const invalidElinkWaterMachineIndex = elinkDraft.waterMachineList.findIndex((item) => {
    return !String(item?.name || '').trim()
      || !String(item?.productModel || '').trim()
      || !String(item?.uuid || '').trim()
      || !String(item?.deviceId || '').trim()
  })
  if (invalidElinkWaterMachineIndex !== -1) {
    eui.showToast(`请完整填写第 ${invalidElinkWaterMachineIndex + 1} 个 eLink 水机的名称、产品型号、UUID 与设备 devID`, 'error')
    return
  }

  if (isDistributedStation.value) {
    const generatedEblocks = buildEblockDraftList()
    const seenEblockUuids = new Set()
    const seenEblockDeviceIds = new Set()
    for (const eb of generatedEblocks) {
      const eblockUuid = (eb.uuid ?? '').trim()
      const eblockDeviceId = (eb.deviceId ?? '').trim()
      if (seenEblockUuids.has(eblockUuid)) {
        eui.showToast('同一储能方阵内 eBlock UUID 不能重复', 'error')
        return
      }
      if (seenEblockDeviceIds.has(eblockDeviceId)) {
        eui.showToast('同一储能方阵内 eBlock 设备 devID 不能重复', 'error')
        return
      }
      seenEblockUuids.add(eblockUuid)
      seenEblockDeviceIds.add(eblockDeviceId)
    }
  }

  const payload = buildElinkPayload()
  const isCentralized = !isDistributedStation.value
  const editingRow = elinkDraft.id != null ? findElinkRow(elinkDraft.id) : null
  const galaxyEquipmentId = editingRow?.galaxyId ?? null
  const galaxyPayload = isCentralized
    ? buildGalaxyPayload(payload, elinkDialog.isEdit ? galaxyEquipmentId : null)
    : null

  elinkDialog.submitting = true
  try {
    if (elinkDialog.isEdit) {
      if (isCentralized) {
        // 集中式：走 Galaxy 编辑接口
        await updateGalaxy(galaxyPayload)
      } else {
        await updateElinkV3(payload)
      }
      eui.showToast('已保存储能方阵', 'success')
    } else {
      if (isCentralized) {
        await addGalaxy(galaxyPayload)
      } else {
        await addElinkV3(payload)
      }
      eui.showToast('已新增储能方阵', 'success')
    }
    closeElinkDialog()
    await loadElinkList()
  } catch (err) {
    console.error('保存储能方阵失败:', err)
    eui.showToast(err?.message || '保存储能方阵失败', 'error')
  } finally {
    elinkDialog.submitting = false
  }
}

function resetMeterDraft() {
  Object.assign(meterDraft, createEmptyMeterDraft())
}

function updateElinkMeteringMeters(elinkId, meters) {
  const idx = elinkRows.value.findIndex((r) => r.id === elinkId)
  if (idx === -1) return false
  const list = [...elinkRows.value]
  list[idx] = {
    ...list[idx],
    meteringMeters: meters.map((m) => ({
      key: m.key || newMeteringMeterKey(),
      name: m.name ?? '',
      uuid: m.uuid ?? '',
      deviceId: m.deviceId ?? '',
    })),
  }
  elinkRows.value = list
  return true
}

function mapElinkFromResponse(item, waterMachineMap, galaxyMeta = null) {
  const badge = item.sort || 0
  const eblocks = Array.isArray(item.eblockList) ? item.eblockList.map((eb, idx) => ({
    key: `eb-${item.equipmentId}-${idx}`,
    equipmentId: eb.equipmentId ?? eb.id ?? eb.eblockId ?? null,
    name: eb.equipmentName || `${item.sort}-${idx + 1}#eBlock`,
    modelTag: eb.productModel || normalizeEblockModel(''),
    uuid: eb.uuid || '',
    deviceId: eb.devId || '',
    maxPower: Number(eb.maxPower) || 0,
    capacity: Number(eb.capacity) || 0,
    waterMachineName: eb.waterMachineName || eb.waterMachineList?.[0]?.waterMachineName || '',
    waterMachineModel: eb.waterMachineProductModel || eb.waterMachineModel || eb.waterMachineList?.[0]?.productModel || '',
    waterMachineUuid: eb.waterMachineUuid || eb.waterMachineList?.[0]?.uuid || '',
    waterMachineDevId: eb.waterMachineDevId || eb.waterMachineList?.[0]?.devId || '',
    waterMachineList: Array.isArray(eb.waterMachineList) ? eb.waterMachineList : [],
    batteryClusterList: Array.isArray(eb.batteryClusterList)
      ? eb.batteryClusterList.map((cl, ci) => ({
        equipmentId: cl.equipmentId ?? cl.id ?? null,
        equipmentName: cl.equipmentName || `${item.sort}-${idx + 1}-${ci + 1}#电池簇`,
        productModel: cl.productModel || '',
        uuid: cl.uuid || '',
        devId: cl.devId || '',
        sort: cl.sort ?? ci + 1,
      }))
      : [],
  })) : []
  const cluster = Array.isArray(item.batteryClusterList) ? item.batteryClusterList.map((cl, idx) => ({
    key: `cl-${item.equipmentId}-${idx}`,
    equipmentId: cl.equipmentId ?? cl.id ?? null,
    name: cl.equipmentName || `${item.sort}-${idx + 1}#电池簇`,
    modelTag: cl.productModel || '',
    uuid: cl.uuid || '',
    deviceId: cl.devId || '',
  })) : []
  const batteryClusterList = Array.isArray(item.batteryClusterList)
    ? item.batteryClusterList.map((cl) => ({
      equipmentId: cl.equipmentId ?? cl.id ?? null,
      equipmentName: cl.equipmentName ?? '',
      productModel: cl.productModel ?? '',
      uuid: cl.uuid ?? '',
      devId: cl.devId ?? '',
      maxPower: cl.maxPower ?? null,
      capacity: cl.capacity ?? null,
      sort: cl.sort ?? 0,
    }))
    : []
  const topLevelWaterMachineList = (waterMachineMap.get(String(item.equipmentId)) || [])
    .map((wm, idx) => normalizeElinkWaterMachineItem({
      id: wm.id,
      parentEquipmentId: wm.parentEquipmentId,
      name: wm.waterMachineName,
      productModel: wm.productModel,
      uuid: wm.uuid,
      deviceId: wm.devId,
    }, badge, idx))
  return {
    id: item.equipmentId,
    galaxyId: galaxyMeta?.equipmentId ?? null,
    galaxyName: galaxyMeta?.equipmentName ?? '',
    galaxyModelTag: galaxyMeta?.productModel ?? '',
    galaxyUuid: galaxyMeta?.uuid ?? '',
    galaxyBatteryClusterList: batteryClusterList,
    name: item.equipmentName || `${item.sort}#能量链`,
    eLinkName: item.equipmentName || '',
    uuid: item.uuid || '',
    modelTag: item.productModel || 'eLink3000',
    badge,
    devId: item.devId || '',
    deviceNetworkId: item.devId || '',
    measuringDistance: item.measuringDistance ?? '',
    vpnConfigured: Number(item.vpnConfigured ?? 0),
    emqxClientId: item.emqxClientId || '',
    waterMachineList: topLevelWaterMachineList,
    meteringMeters: Array.isArray(item.meterList) ? item.meterList.map((m, idx) => ({
      id: m.id ?? null,
      stationId: m.stationId ?? props.stationId ?? '',
      meterType: m.meterType ?? 2,
      productModel: m.productModel ?? '2',
      productRatio: m.productRatio ?? 1,
      productLocation: m.productLocation ?? '1',
      linkEquipmentUuid: m.linkEquipmentUuid ?? item.uuid ?? '',
      key: `meter-${item.equipmentId}-${idx}`,
      name: m.meterName || '',
      uuid: m.uuid || '',
      deviceId: m.devId || '',
    })) : [],
    eblocks,
    cluster,
  }
}

async function loadElinkList() {
  if (!props.stationId) {
    elinkRows.value = []
    return
  }
  try {
    const waterMachineList = await loadWaterMachineList()
    const waterMachineMap = buildWaterMachineMapByParent(waterMachineList)
    if (isDistributedStation.value) {
      const elinkRes = await getElinkListV3({
        stationId: props.stationId,
        pageNum: 1,
        pageSize: 200,
      })
      const list = elinkRes?.rows ?? elinkRes?.data?.rows ?? elinkRes?.data ?? []
      elinkRows.value = Array.isArray(list) ? list.map((item) => mapElinkFromResponse(item, waterMachineMap)) : []
    } else {
      // 集中式：使用 Galaxy 树接口 /device/v3/galaxy/tree
      const galaxyRes = await getGalaxyTree(props.stationId)
      const galaxyList = galaxyRes?.rows ?? galaxyRes?.data?.rows ?? galaxyRes?.data ?? []
      const flattened = []
      if (Array.isArray(galaxyList)) {
        galaxyList.forEach((galaxy) => {
          const galaxyMeta = {
            equipmentId: galaxy?.equipmentId ?? galaxy?.id ?? null,
            equipmentName: galaxy?.equipmentName ?? '',
            productModel: galaxy?.productModel ?? '',
            uuid: galaxy?.uuid ?? '',
            sort: galaxy?.sort ?? 0,
          }
          const nestedElinks = Array.isArray(galaxy?.elinkList) ? galaxy.elinkList : []
          if (!nestedElinks.length) {
            return
          }
          nestedElinks.forEach((elink) => {
            flattened.push(mapElinkFromResponse(elink, waterMachineMap, galaxyMeta))
          })
        })
      }
      elinkRows.value = flattened
    }
  } catch (err) {
    console.error('获取储能方阵列表失败:', err)
  }
}

async function loadMeterList() {
  if (!props.stationId) {
    meterRows.value = []
    return
  }
  try {
    const res = await getMeterList({
      stationId: props.stationId,
      pageNum: 1,
      pageSize: 200,
    })
    const list = res?.rows ?? res?.data?.rows ?? res?.data ?? []
    meterRows.value = Array.isArray(list) ? list : []
  } catch (err) {
    console.error('获取电表列表失败:', err)
  }
}

function openMeterAdd() {
  meterDialog.isEdit = false
  meterDialog.elinkId = null
  meterDialog.meterKey = ''
  resetMeterDraft()
  loadMeterModels()
  meterDialog.open = true
}

function openMeterEdit(row) {
  meterDialog.isEdit = true
  meterDialog.elinkId = null
  meterDialog.meterKey = ''
  resetMeterDraft()
  loadMeterModels()
  Object.assign(meterDraft, {
    id: row.id ?? null,
    uuid: row.uuid ?? '',
    devId: row.devId ?? '',
    stationId: row.stationId ?? props.stationId ?? '',
    unitId: row.unitId ?? 1,
    meterName: row.meterName ?? '',
    meterType: row.meterType != null ? Number(row.meterType) : '',
    commInterface: row.commInterface ?? '',
    factoryName: row.factoryName ?? '',
    productModel: normalizeMeterModel(row.productModel),
    productRatio: row.productRatio ?? '',
    productLocation: row.productLocation != null ? String(row.productLocation) : '0',
    imageUrl: row.imageUrl ?? '',
    productDescription: row.productDescription ?? '',
    measuringEquipIdStr: row.measuringEquipIdStr ?? '',
    assetCode: row.assetCode ?? '',
    assetProperty: row.assetProperty ?? '',
    assetUnit: row.assetUnit ?? '',
    parentMeterUuid: row.parentMeterUuid ?? '',
    linkEquipmentUuid: row.linkEquipmentUuid ?? '',
    sort: row.sort != null ? Number(row.sort) : 0,
  })
  meterDialog.open = true
}

function closeMeterDialog() {
  meterDialog.open = false
  meterDialog.elinkId = null
  meterDialog.meterKey = ''
}

function buildMeterPayload() {
  const trim = (v) => (typeof v === 'string' ? v.trim() : v)
  return {
    stationId: props.stationId,
    uuid: trim(meterDraft.uuid) || undefined,
    devId: trim(meterDraft.devId) || undefined,
    unitId: 1,
    meterName: trim(meterDraft.meterName) || undefined,
    meterType: Number(meterDraft.meterType),
    commInterface: trim(meterDraft.commInterface) || undefined,
    factoryName: trim(meterDraft.factoryName) || undefined,
    productModel: trim(meterDraft.productModel),
    productRatio: Number(meterDraft.productRatio),
    productLocation: trim(meterDraft.productLocation),
    imageUrl: trim(meterDraft.imageUrl) || undefined,
    productDescription: trim(meterDraft.productDescription) || undefined,
    measuringEquipIdStr: trim(meterDraft.measuringEquipIdStr) || undefined,
    assetCode: trim(meterDraft.assetCode) || undefined,
    assetProperty: trim(meterDraft.assetProperty) || undefined,
    assetUnit: trim(meterDraft.assetUnit) || undefined,
    parentMeterUuid: trim(meterDraft.parentMeterUuid) || undefined,
    linkEquipmentUuid: trim(meterDraft.linkEquipmentUuid) || undefined,
    sort: meterDraft.sort != null && meterDraft.sort !== '' ? Number(meterDraft.sort) : undefined,
  }
}

async function submitMeter() {
  if (!props.stationId) {
    eui.showToast('请先完成基本信息以获取电站ID', 'error')
    return
  }
  if (meterDialog.elinkId != null) {
    const name = meterDraft.meterName?.trim()
    const uuid = meterDraft.uuid?.trim()
    const deviceId = meterDraft.devId?.trim()
    if (!name || !uuid || !deviceId) {
      eui.showToast('请完整填写计量电表名称、UUID 与设备 devID', 'error')
      return
    }
    const row = findElinkRow(meterDialog.elinkId)
    if (!row) return
    const payload = {
      stationId: props.stationId,
      meterName: name,
      meterType: 2,
      uuid,
      devId: deviceId,
      productModel: '2',
      productRatio: 1,
      productLocation: '1',
      linkEquipmentUuid: row.uuid?.trim() || undefined,
    }
    meterSubmitting.value = true
    try {
      if (meterDialog.isEdit && meterDraft.id != null) {
        await updateMeter({ ...payload, id: meterDraft.id })
        eui.showToast('已保存计量电表', 'success')
      } else {
        await addMeter(payload)
        eui.showToast('已新增计量电表', 'success')
      }
      closeMeterDialog()
      await loadElinkList()
    } catch (err) {
      console.error(`${meterDialog.isEdit ? '保存' : '新增'}计量电表失败:`, err)
      eui.showToast(err?.message || `${meterDialog.isEdit ? '保存' : '新增'}计量电表失败`, 'error')
    } finally {
      meterSubmitting.value = false
    }
    return
  }

  const name = meterDraft.meterName?.trim()
  const productModel = meterDraft.productModel?.trim()
  const productRatio = Number(meterDraft.productRatio)
  const productLocation = meterDraft.productLocation?.trim()
  const uuid = meterDraft.uuid?.trim()
  if (!meterDraft.meterType) {
    eui.showToast('请选择电表类型', 'error')
    return
  }
  if (!productModel) {
    eui.showToast('请选择产品型号', 'error')
    return
  }
  if (!name) {
    eui.showToast('请输入电表名称', 'error')
    return
  }
  if (!uuid) {
    eui.showToast('UUID 未生成，请重新选择电表类型', 'error')
    return
  }
  if (!Number.isInteger(productRatio) || productRatio < 0 || productRatio > 99999) {
    eui.showToast('请填写 0-99999 的电表变比', 'error')
    return
  }
  if (!productLocation) {
    eui.showToast('请选择安装位置', 'error')
    return
  }
  const payload = buildMeterPayload()
  meterSubmitting.value = true
  try {
    if (meterDialog.isEdit) {
      await updateMeter({ ...payload, id: meterDraft.id })
      eui.showToast('已保存电表', 'success')
    } else {
      await addMeter(payload)
      eui.showToast('已新增电表', 'success')
    }
    closeMeterDialog()
    await loadMeterList()
  } catch (err) {
    console.error('保存电表失败:', err)
  } finally {
    meterSubmitting.value = false
  }
}

function resetTransformerDraft() {
  Object.assign(transformerDraft, createEmptyTransformerDraft())
}

async function loadTransformerList() {
  if (!props.stationId) {
    transformerRows.value = []
    return
  }
  try {
    const res = await getTransformerList({
      stationId: props.stationId,
      pageNum: 1,
      pageSize: 200,
    })
    const list = res?.rows ?? res?.data?.rows ?? res?.data ?? []
    transformerRows.value = Array.isArray(list) ? list : []
  } catch (err) {
    console.error('获取变压器列表失败:', err)
  }
}

function openTransformerAdd() {
  transformerDialog.isEdit = false
  resetTransformerDraft()
  transformerDraft.uuid = buildTransformerUuid(nextTransformerOrder())
  transformerDialog.open = true
}

function openTransformerEdit(row) {
  transformerDialog.isEdit = true
  resetTransformerDraft()
  Object.assign(transformerDraft, {
    id: row.id ?? null,
    stationId: row.stationId ?? props.stationId ?? '',
    uuid: row.uuid ?? '',
    devId: row.devId ?? '',
    unitId: row.unitId ?? 1,
    transformerName: row.transformerName ?? '',
    manufactorName: row.manufactorName ?? '',
    productModel: row.productModel ?? '',
    serialNumber: row.serialNumber ?? '',
    manufactureTime: row.manufactureTime ?? '',
    ratedCapacity: row.ratedCapacity ?? '',
    voltageLevel: row.voltageLevel ?? '',
    inputVoltage: row.inputVoltage ?? '',
    outputVoltage: row.outputVoltage ?? '',
    medium: row.medium ?? '',
    coreStructure: row.coreStructure ?? '',
    sort: row.sort ?? 0,
  })
  transformerDialog.open = true
}

function closeTransformerDialog() {
  transformerDialog.open = false
}

function buildTransformerPayload() {
  const trim = (v) => (typeof v === 'string' ? v.trim() : v)
  const parsedSort = transformerDraft.sort === '' || transformerDraft.sort == null
    ? undefined
    : Number(transformerDraft.sort)
  return {
    stationId: props.stationId,
    uuid: trim(transformerDraft.uuid),
    devId: trim(transformerDraft.devId),
    unitId: 1,
    transformerName: trim(transformerDraft.transformerName) || undefined,
    manufactorName: trim(transformerDraft.manufactorName) || undefined,
    productModel: trim(transformerDraft.productModel),
    serialNumber: trim(transformerDraft.serialNumber) || undefined,
    manufactureTime: trim(transformerDraft.manufactureTime) || undefined,
    ratedCapacity: trim(transformerDraft.ratedCapacity) || undefined,
    voltageLevel: trim(transformerDraft.voltageLevel) || undefined,
    inputVoltage: trim(transformerDraft.inputVoltage) || undefined,
    outputVoltage: trim(transformerDraft.outputVoltage) || undefined,
    medium: trim(transformerDraft.medium) || undefined,
    coreStructure: trim(transformerDraft.coreStructure) || undefined,
    sort: Number.isNaN(parsedSort) ? undefined : parsedSort,
  }
}

async function submitTransformer() {
  if (!props.stationId) {
    eui.showToast('请先完成基本信息以获取电站ID', 'error')
    return
  }
  const name = transformerDraft.transformerName?.trim()
  const devId = transformerDraft.devId?.trim()
  const uuid = transformerDraft.uuid?.trim()
  const productModel = transformerDraft.productModel?.trim()
  const ratedCapacity = transformerDraft.ratedCapacity?.trim()
  if (!productModel) {
    eui.showToast('请填写产品型号', 'error')
    return
  }
  if (!name) {
    eui.showToast('请填写变压器名称', 'error')
    return
  }
  if (!devId) {
    eui.showToast('请填写设备devID', 'error')
    return
  }
  if (!ratedCapacity) {
    eui.showToast('请填写额定容量', 'error')
    return
  }
  if (!uuid) {
    eui.showToast('UUID 未生成，请稍后重试', 'error')
    return
  }
  const payload = buildTransformerPayload()
  transformerSubmitting.value = true
  try {
    if (transformerDialog.isEdit) {
      await updateTransformer({ ...payload, id: transformerDraft.id })
      eui.showToast('已保存变压器', 'success')
    } else {
      await addTransformer(payload)
      eui.showToast('已新增变压器', 'success')
    }
    closeTransformerDialog()
    await loadTransformerList()
  } catch (err) {
    console.error('保存变压器失败:', err)
  } finally {
    transformerSubmitting.value = false
  }
}

function makeSimpleDeviceHandlers({ rows, dialog, draft, kindLabel, kindKey }) {
  function resetDraft() {
    draft.id = ''
    draft.name = ''
    draft.model = ''
    draft.uuid = ''
  }
  function openAdd() {
    dialog.isEdit = false
    resetDraft()
    dialog.open = true
  }
  function openEdit(row) {
    dialog.isEdit = true
    draft.id = row.id
    draft.name = row.name
    draft.model = row.model ?? ''
    draft.uuid = row.uuid
    dialog.open = true
  }
  function close() {
    dialog.open = false
  }
  function submit() {
    const name = draft.name?.trim()
    const uuid = draft.uuid?.trim()
    if (!name) {
      eui.showToast(`请填写${kindLabel}名称`, 'error')
      return
    }
    if (!uuid) {
      eui.showToast('请填写 UUID', 'error')
      return
    }
    const model = (draft.model ?? '').trim()
    const dup = rows.value.some((r) => r.uuid === uuid && r.id !== draft.id)
    if (dup) {
      eui.showToast('UUID 已存在', 'error')
      return
    }
    if (dialog.isEdit) {
      const idx = rows.value.findIndex((r) => r.id === draft.id)
      if (idx === -1) return
      const list = [...rows.value]
      list[idx] = { ...list[idx], name, uuid, model }
      rows.value = list
      eui.showToast(`已保存${kindLabel}`, 'success')
    } else {
      rows.value = [...rows.value, { id: genSimpleId(kindKey), name, uuid, model }]
      eui.showToast(`已新增${kindLabel}`, 'success')
    }
    close()
  }
  return { openAdd, openEdit, close, submit }
}

async function loadConditionerModels() {
  if (conditionerModelOptions.value.length || conditionerModelLoading.value) return
  conditionerModelLoading.value = true
  try {
    const res = await listWaterMachineModel({ pageNum: 1, pageSize: 200 })
    const list = res?.rows ?? res?.data?.rows ?? res?.data ?? []
    conditionerModelRaw.value = Array.isArray(list) ? list : []
    conditionerModelOptions.value = conditionerModelRaw.value
      .map((m) => ({
        title: m.productModel || m.modelName || '',
        value: m.productModel || m.modelName || '',
        raw: m,
      }))
      .filter((o) => o.value)
  } catch (err) {
    console.error('拉取水机型号失败:', err)
  } finally {
    conditionerModelLoading.value = false
  }
}

function resetPvDraft() {
  Object.assign(pvDraft, createEmptyPvDraft())
}

async function loadPvModels() {
  if (pvModelOptions.value.length || pvModelLoading.value) return
  pvModelLoading.value = true
  try {
    const res = await listPhotovoltaicModel({ pageNum: 1, pageSize: 200 })
    const list = res?.rows ?? res?.data?.rows ?? res?.data ?? []
    pvModelRaw.value = Array.isArray(list) ? list : []
    pvModelOptions.value = pvModelRaw.value
      .map((m) => ({
        title: m.productModel || m.modelName || '',
        value: m.productModel || m.modelName || '',
        raw: m,
      }))
      .filter((o) => o.value)
  } catch (err) {
    console.error('拉取光伏型号失败:', err)
  } finally {
    pvModelLoading.value = false
  }
}

function onPvModelChange(val) {
  const matched = pvModelRaw.value.find((m) => (m.productModel || m.modelName) === val)
  if (!matched) {
    clearPvDerivedFields()
    return
  }
  pvDraft.factoryName = matched.factoryName ?? ''
  pvDraft.maxDcVoltage = matched.maxDcVoltage != null ? String(matched.maxDcVoltage) : ''
  pvDraft.maxDcPower = matched.maxDcPower != null ? String(matched.maxDcPower) : ''
  pvDraft.ratedOutputPower = matched.ratedOutputPower != null ? String(matched.ratedOutputPower) : ''
  pvDraft.maxOutputPower = matched.maxOutputPower != null ? String(matched.maxOutputPower) : ''
  pvDraft.maxAcOutputCurrent = matched.maxAcOutputCurrent != null ? String(matched.maxAcOutputCurrent) : ''
  pvDraft.maxInputCurrent = matched.maxInputCurrent != null ? String(matched.maxInputCurrent) : ''
  pvDraft.ratedGridVoltage = matched.ratedGridVoltage != null ? String(matched.ratedGridVoltage) : ''
  pvDraft.ratedGridFrequency = matched.ratedGridFrequency != null ? String(matched.ratedGridFrequency) : ''
}

function clearPvDerivedFields() {
  pvDraft.factoryName = ''
  pvDraft.maxDcVoltage = ''
  pvDraft.maxDcPower = ''
  pvDraft.ratedOutputPower = ''
  pvDraft.maxOutputPower = ''
  pvDraft.maxAcOutputCurrent = ''
  pvDraft.maxInputCurrent = ''
  pvDraft.ratedGridVoltage = ''
  pvDraft.ratedGridFrequency = ''
}

async function loadPvList() {
  if (!props.stationId) {
    pvRows.value = []
    return
  }
  try {
    const res = await getPvList({
      stationId: props.stationId,
      pageNum: 1,
      pageSize: 200,
    })
    const list = res?.rows ?? res?.data?.rows ?? res?.data ?? []
    pvRows.value = Array.isArray(list) ? list : []
  } catch (err) {
    console.error('获取光伏列表失败:', err)
  }
}

function openPvAdd() {
  pvDialog.isEdit = false
  resetPvDraft()
  pvDraft.uuid = buildPvUuid(nextPvOrder())
  loadPvModels()
  pvDialog.open = true
}

function openPvEdit(row) {
  pvDialog.isEdit = true
  resetPvDraft()
  loadPvModels()
  Object.assign(pvDraft, {
    id: row.id ?? null,
    uuid: row.uuid ?? '',
    devId: row.devId ?? '',
    photovoltaicName: row.photovoltaicName ?? '',
    factoryName: row.factoryName ?? '',
    productModel: row.productModel ?? '',
    parentEquipmentId: row.parentEquipmentId != null ? String(row.parentEquipmentId) : '',
    maxDcVoltage: row.maxDcVoltage ?? '',
    maxDcPower: row.maxDcPower ?? '',
    ratedOutputPower: row.ratedOutputPower ?? '',
    maxOutputPower: row.maxOutputPower ?? '',
    maxAcOutputCurrent: row.maxAcOutputCurrent ?? '',
    maxInputCurrent: row.maxInputCurrent ?? '',
    ratedGridVoltage: row.ratedGridVoltage ?? '',
    ratedGridFrequency: row.ratedGridFrequency ?? '',
    sort: row.sort != null ? Number(row.sort) : 0,
  })
  pvDialog.open = true
}

function closePvDialog() {
  pvDialog.open = false
}

function buildPvPayload() {
  const trim = (v) => (typeof v === 'string' ? v.trim() : v)
  return {
    stationId: props.stationId,
    unitId: 1,
    uuid: trim(pvDraft.uuid),
    devId: trim(pvDraft.devId) || undefined,
    photovoltaicName: trim(pvDraft.photovoltaicName),
    factoryName: trim(pvDraft.factoryName) || undefined,
    productModel: trim(pvDraft.productModel) || undefined,
    parentEquipmentId: trim(pvDraft.parentEquipmentId) || undefined,
    maxDcVoltage: trim(pvDraft.maxDcVoltage) || undefined,
    maxDcPower: trim(pvDraft.maxDcPower) || undefined,
    ratedOutputPower: trim(pvDraft.ratedOutputPower) || undefined,
    maxOutputPower: trim(pvDraft.maxOutputPower) || undefined,
    maxAcOutputCurrent: trim(pvDraft.maxAcOutputCurrent) || undefined,
    maxInputCurrent: trim(pvDraft.maxInputCurrent) || undefined,
    ratedGridVoltage: trim(pvDraft.ratedGridVoltage) || undefined,
    ratedGridFrequency: trim(pvDraft.ratedGridFrequency) || undefined,
    sort: pvDraft.sort != null && pvDraft.sort !== '' ? Number(pvDraft.sort) : undefined,
  }
}

async function submitPv() {
  if (!props.stationId) {
    eui.showToast('请先完成基本信息以获取电站ID', 'error')
    return
  }
  const productModel = pvDraft.productModel?.trim()
  const name = pvDraft.photovoltaicName?.trim()
  const uuid = pvDraft.uuid?.trim()
  if (!productModel) {
    eui.showToast('请选择产品型号', 'error')
    return
  }
  if (!name) {
    eui.showToast('请输入光伏名称', 'error')
    return
  }
  if (!uuid) {
    eui.showToast('请填写 UUID', 'error')
    return
  }
  if (!pvDraft.parentEquipmentId?.trim()) {
    eui.showToast('请选择关联设备devID', 'error')
    return
  }
  const payload = buildPvPayload()
  pvSubmitting.value = true
  try {
    if (pvDialog.isEdit) {
      await updatePv({ ...payload, id: pvDraft.id })
      eui.showToast('已保存光伏', 'success')
    } else {
      await addPv(payload)
      eui.showToast('已新增光伏', 'success')
    }
    closePvDialog()
    await loadPvList()
  } catch (err) {
    console.error('保存光伏失败:', err)
  } finally {
    pvSubmitting.value = false
  }
}

function resetPileDraft() {
  Object.assign(pileDraft, createEmptyPileDraft())
}

async function loadPileList() {
  if (!props.stationId) {
    pileRows.value = []
    return
  }
  try {
    const res = await getChargePileList({
      stationId: props.stationId,
      pageNum: 1,
      pageSize: 200,
    })
    const list = res?.rows ?? res?.data?.rows ?? res?.data ?? []
    pileRows.value = Array.isArray(list) ? list : []
  } catch (err) {
    console.error('获取充电桩列表失败:', err)
  }
}

function openPileAdd() {
  pileDialog.isEdit = false
  resetPileDraft()
  pileDraft.uuid = buildChargePileUuid(nextChargePileOrder())
  loadPileModels()
  pileDialog.open = true
}

function openPileEdit(row) {
  pileDialog.isEdit = true
  resetPileDraft()
  loadPileModels()
  Object.assign(pileDraft, {
    id: row.id ?? null,
    uuid: row.uuid ?? '',
    devId: row.devId ?? '',
    chargePileName: row.chargePileName ?? '',
    factoryName: row.factoryName ?? '',
    productModel: row.productModel ?? '',
    parentEquipmentId: row.parentEquipmentId != null ? String(row.parentEquipmentId) : '',
    inputVoltage: row.inputVoltage ?? '',
    outputVoltage: row.outputVoltage ?? '',
    ratedOutputPower: row.ratedOutputPower ?? '',
    fullLoadPower: row.fullLoadPower ?? '',
    chargeInterface: row.chargeInterface ?? '',
    chargeMode: row.chargeMode != null && row.chargeMode !== '' ? Number(row.chargeMode) : '',
    sort: row.sort != null ? Number(row.sort) : 0,
  })
  pileDialog.open = true
}

function closePileDialog() {
  pileDialog.open = false
}

function buildPilePayload() {
  const trim = (v) => (typeof v === 'string' ? v.trim() : v)
  return {
    stationId: props.stationId,
    unitId: 1,
    uuid: trim(pileDraft.uuid),
    devId: trim(pileDraft.devId) || undefined,
    chargePileName: trim(pileDraft.chargePileName),
    factoryName: trim(pileDraft.factoryName) || undefined,
    productModel: trim(pileDraft.productModel) || undefined,
    parentEquipmentId: trim(pileDraft.parentEquipmentId) || undefined,
    inputVoltage: trim(pileDraft.inputVoltage) || undefined,
    outputVoltage: trim(pileDraft.outputVoltage) || undefined,
    ratedOutputPower: trim(pileDraft.ratedOutputPower) || undefined,
    fullLoadPower: trim(pileDraft.fullLoadPower) || undefined,
    chargeInterface: trim(pileDraft.chargeInterface) || undefined,
    chargeMode: pileDraft.chargeMode != null && pileDraft.chargeMode !== '' ? Number(pileDraft.chargeMode) : undefined,
    sort: pileDraft.sort != null && pileDraft.sort !== '' ? Number(pileDraft.sort) : undefined,
  }
}

async function submitPile() {
  if (!props.stationId) {
    eui.showToast('请先完成基本信息以获取电站ID', 'error')
    return
  }
  const productModel = pileDraft.productModel?.trim()
  const name = pileDraft.chargePileName?.trim()
  const uuid = pileDraft.uuid?.trim()
  if (!productModel) {
    eui.showToast('请选择产品型号', 'error')
    return
  }
  if (!name) {
    eui.showToast('请输入充电桩名称', 'error')
    return
  }
  if (!uuid) {
    eui.showToast('请填写 UUID', 'error')
    return
  }
  if (!pileDraft.parentEquipmentId?.trim()) {
    eui.showToast('请选择关联设备devID', 'error')
    return
  }
  const payload = buildPilePayload()
  pileSubmitting.value = true
  try {
    if (pileDialog.isEdit) {
      await updateChargePile({ ...payload, id: pileDraft.id })
      eui.showToast('已保存充电桩', 'success')
    } else {
      await addChargePile(payload)
      eui.showToast('已新增充电桩', 'success')
    }
    closePileDialog()
    await loadPileList()
  } catch (err) {
    console.error('保存充电桩失败:', err)
  } finally {
    pileSubmitting.value = false
  }
}

function askDeletePv(row) {
  deleteDialog.kind = 'pv'
  deleteDialog.title = '删除光伏'
  deleteDialog.target = row
  deleteDialog.open = true
}

function askDeletePile(row) {
  deleteDialog.kind = 'pile'
  deleteDialog.title = '删除充电桩'
  deleteDialog.target = row
  deleteDialog.open = true
}

/** 使用数组而非 Set，避免模板中 .has() 与 v-expand-transition 组合时的更新/折叠异常 */
const expandedElinkIds = ref([])

function isElinkExpanded(id) {
  return expandedElinkIds.value.includes(id)
}
function resetEblockDraft() {
  eblockDraft.elinkId = null
  eblockDraft.key = ''
  eblockDraft.name = ''
  eblockDraft.modelTag = normalizeEblockModel('')
  eblockDraft.uuid = ''
  eblockDraft.deviceId = ''
  eblockDraft.waterMachineName = ''
  eblockDraft.waterMachineModel = ''
  eblockDraft.waterMachineUuid = ''
  eblockDraft.waterMachineDevId = ''
}

function suggestNextEblockDefaults(elinkRow) {
  const elinkOrder = Math.max(0, Number(elinkRow.badge ?? elinkRow.id) || 0)
  const list = eblocksForRow(elinkRow)
  const nextIdx = nextEblockOrder(list)
  const modelTag = list[list.length - 1]?.modelTag || normalizeEblockModel('')
  const base = {
    name: `${elinkOrder}-${nextIdx}#eBlock`,
    uuid: buildEblockUuid(elinkOrder, nextIdx),
    deviceId: '',
    modelTag,
  }
  const waterMachineModel = list[list.length - 1]?.waterMachineModel || ''
  if (isLiquidCooledEblockModel(modelTag) && waterMachineModel) {
    return {
      ...base,
      ...buildWaterMachineMeta(elinkOrder, nextIdx, waterMachineModel),
    }
  }
  return base
}

function openEblockAdd(elinkRow) {
  eblockDialog.isEdit = false
  resetEblockDraft()
  eblockDraft.elinkId = elinkRow.id
  Object.assign(eblockDraft, suggestNextEblockDefaults(elinkRow))
  if (shouldShowEblockWaterMachineFields.value) {
    eblockDraft.waterMachineModel = elinkRow.eblocks?.[0]?.waterMachineModel ?? ''
    syncEblockWaterMachineDraft()
  }
  loadEblockModels()
  loadConditionerModels()
  eblockDialog.open = true
}

function openEblockEdit(elinkRow, eb) {
  eblockDialog.isEdit = true
  eblockDraft.elinkId = elinkRow.id
  eblockDraft.key = eb.key
  eblockDraft.name = eb.name
  eblockDraft.modelTag = normalizeEblockModel(eb.modelTag)
  eblockDraft.uuid = eb.uuid
  eblockDraft.deviceId = eb.deviceId
  eblockDraft.waterMachineName = eb.waterMachineName || ''
  eblockDraft.waterMachineModel = eb.waterMachineModel || ''
  eblockDraft.waterMachineUuid = eb.waterMachineUuid || ''
  eblockDraft.waterMachineDevId = eb.waterMachineDevId || ''
  loadEblockModels()
  loadConditionerModels()
  syncEblockWaterMachineDraft()
  eblockDialog.open = true
}

function closeEblockDialog() {
  eblockDialog.open = false
}

async function submitEblock() {
  if (!props.stationId) {
    eui.showToast('请先完成基本信息以获取电站ID', 'error')
    return
  }

  const elinkId = eblockDraft.elinkId
  const elinkRow = findElinkRow(elinkId)
  if (!elinkRow) return

  const list = [...eblocksForRow(elinkRow)]

  const name = eblockDraft.name?.trim()
  const uuid = eblockDraft.uuid?.trim()
  const deviceId = eblockDraft.deviceId?.trim()
  const modelTag = normalizeEblockModel(eblockDraft.modelTag)
  const waterMachineModel = eblockDraft.waterMachineModel?.trim()
  const waterMachineName = eblockDraft.waterMachineName?.trim()
  const waterMachineUuid = eblockDraft.waterMachineUuid?.trim()
  const waterMachineDevId = eblockDraft.waterMachineDevId?.trim()
  if (!name || !uuid || !deviceId) {
    eui.showToast('请填写 eBlock 名称、UUID 与设备 devID', 'error')
    return
  }
  if (shouldShowEblockWaterMachineFields.value) {
    if (!waterMachineName || !waterMachineModel || !waterMachineUuid || !waterMachineDevId) {
      eui.showToast('液冷 eBlock 请完整填写水机名称、产品型号、UUID 与设备 devID', 'error')
      return
    }
  }

  const item = {
    key: eblockDraft.key || newEblockKey(),
    name,
    modelTag,
    uuid,
    deviceId,
    waterMachineName: waterMachineName || '',
    waterMachineModel: waterMachineModel || '',
    waterMachineUuid: waterMachineUuid || '',
    waterMachineDevId: waterMachineDevId || '',
  }
  const { maxPower, capacity } = getEblockModelMetrics(modelTag)
  const elinkOrder = Math.max(0, Number(elinkRow?.badge) || 0)

  if (eblockDialog.isEdit) {
    const dupUuid = list.some((eb) => eb.uuid === uuid && eb.key !== eblockDraft.key)
    if (dupUuid) {
      eui.showToast('同一储能方阵内 eBlock UUID 不能重复', 'error')
      return
    }
    const dupDeviceId = list.some((eb) => eb.deviceId === deviceId && eb.key !== eblockDraft.key)
    if (dupDeviceId) {
      eui.showToast('同一储能方阵内 eBlock 设备 devID 不能重复', 'error')
      return
    }

    const idx = list.findIndex((eb) => eb.key === eblockDraft.key)
    if (idx === -1) return
    const current = rowEblocksSource(elinkRow)[idx]
    const equipmentId = current?.equipmentId ?? current?.id ?? current?.eblockId ?? null
    if (!equipmentId) {
      eui.showToast('未找到 eBlock 设备devID，无法保存', 'error')
      return
    }

    // 编辑态：保留已回显的电池簇（前端不允许改），若无则按型号 clusterCount 补齐
    const eblockOrder = idx + 1
    const stationIdNum = Number(props.stationId)
    const clusters = buildEblockBatteryClusterList(
      elinkOrder,
      eblockOrder,
      modelTag,
      current?.batteryClusterList,
    )
    const batteryClusterList = clusters.map((cl) => {
      const payload = {
        equipmentName: cl.equipmentName,
        equipmentTypeId: 5,
        stationId: stationIdNum,
        unitId: 1,
        productModel: cl.productModel,
        uuid: cl.uuid,
        devId: cl.devId,
        parentEquipmentId: equipmentId,
        sort: cl.sort,
      }
      if (cl.equipmentId != null) payload.equipmentId = cl.equipmentId
      return payload
    })

    try {
      const updatePayload = {
        equipmentId,
        stationId: props.stationId,
        unitId: 1,
        parentEquipmentId: Number(elinkId),
        equipmentName: name,
        equipmentTypeId: 3,
        productModel: modelTag,
        maxPower,
        capacity,
        uuid,
        devId: deviceId,
        sort: idx + 1,
        waterMachineList: item.waterMachineModel && item.waterMachineUuid && item.waterMachineDevId
          ? [{
            stationId: Number(props.stationId),
            unitId: 1,
            uuid: item.waterMachineUuid,
            devId: item.waterMachineDevId,
            waterMachineName: item.waterMachineName || undefined,
            productModel: item.waterMachineModel || undefined,
            parentEquipmentId: equipmentId,
            sort: 1,
          }]
          : [],
      }
      if (batteryClusterList.length) updatePayload.batteryClusterList = batteryClusterList
      await updateBlockV3(updatePayload)
      eui.showToast('已保存 eBlock', 'success')
      closeEblockDialog()
      await loadElinkList()
    } catch (err) {
      console.error('保存 eBlock 失败:', err)
      eui.showToast(err?.message || '保存 eBlock 失败', 'error')
    }
    return
  }

  const dupUuid = list.some((eb) => eb.uuid === uuid)
  if (dupUuid) {
    eui.showToast('同一储能方阵内 eBlock UUID 不能重复', 'error')
    return
  }
  const dupDeviceId = list.some((eb) => eb.deviceId === deviceId)
  if (dupDeviceId) {
    eui.showToast('同一储能方阵内 eBlock 设备 devID 不能重复', 'error')
    return
  }

  // 新增态：根据 eBlock 型号的 clusterCount 自动生成 batteryClusterList
  const newEblockOrder = list.length + 1
  const stationIdNum = Number(props.stationId)
  const newClusters = buildEblockBatteryClusterList(elinkOrder, newEblockOrder, modelTag, [])
  const newBatteryClusterList = newClusters.map((cl) => ({
    equipmentName: cl.equipmentName,
    equipmentTypeId: 5,
    stationId: stationIdNum,
    unitId: 1,
    productModel: cl.productModel,
    uuid: cl.uuid,
    devId: cl.devId,
    sort: cl.sort,
  }))

  try {
    const addPayload = {
      stationId: props.stationId,
      unitId: 1,
      parentEquipmentId: Number(elinkId),
      equipmentName: name,
      equipmentTypeId: 3,
      productModel: modelTag,
      maxPower,
      capacity,
      uuid,
      devId: deviceId,
      sort: list.length + 1,
      waterMachineList: [{
        stationId: Number(props.stationId),
        unitId: 1,
        uuid: item.waterMachineUuid,
        devId: item.waterMachineDevId,
        waterMachineName: item.waterMachineName || undefined,
        productModel: item.waterMachineModel || undefined,
        parentEquipmentId: Number(elinkId),
        sort: 1,
      }],
    }
    if (newBatteryClusterList.length) addPayload.batteryClusterList = newBatteryClusterList
    await addBlockV3(addPayload)
    eui.showToast('已新增 eBlock', 'success')
    closeEblockDialog()
    await loadElinkList()
  } catch (err) {
    console.error('新增 eBlock 失败:', err)
    eui.showToast(err?.message || '新增 eBlock 失败', 'error')
  }
}

function askDeleteEblock(elinkRow, eb) {
  deleteDialog.kind = 'eblock'
  deleteDialog.title = '删除 eBlock'
  deleteDialog.target = { elinkId: elinkRow.id, eblock: eb }
  deleteDialog.open = true
}

function askDeleteElink(row) {
  deleteDialog.kind = 'elink'
  deleteDialog.title = '删除储能方阵'
  deleteDialog.target = row
  deleteDialog.open = true
}

function askDeleteMeter(row) {
  deleteDialog.kind = 'meter'
  deleteDialog.title = '删除电表'
  deleteDialog.target = row
  deleteDialog.open = true
}

function askDeleteTransformer(row) {
  deleteDialog.kind = 'transformer'
  deleteDialog.title = '删除变压器'
  deleteDialog.target = row
  deleteDialog.open = true
}

function closeDeleteDialog() {
  deleteDialog.open = false
  deleteDialog.target = null
  deleteDialog.kind = ''
}

async function confirmDelete() {
  const t = deleteDialog.target
  if (!t) return
  if (deleteDialog.kind === 'elink') {
    const isCentralized = !isDistributedStation.value
    // 集中式：删除 Galaxy（/device/v3/galaxy/{equipmentId}）；分散式：删除能量链
    const deleteId = isCentralized ? (t.galaxyId ?? t.id) : t.id
    if (deleteId != null) {
      try {
        if (isCentralized) {
          await deleteGalaxy(deleteId)
        } else {
          await delElinkV3(deleteId)
        }
        elinkRows.value = elinkRows.value.filter((r) => r.id !== t.id)
        expandedElinkIds.value = expandedElinkIds.value.filter((x) => x !== t.id)
        eui.showToast('已删除储能方阵', 'success')
      } catch (err) {
        console.error('删除储能方阵失败:', err)
      }
    } else {
      elinkRows.value = elinkRows.value.filter((r) => r.id !== t.id)
      expandedElinkIds.value = expandedElinkIds.value.filter((x) => x !== t.id)
      eui.showToast('已删除储能方阵', 'success')
    }
  } else if (deleteDialog.kind === 'eblock') {
    const elinkId = t.elinkId
    const key = t.eblock?.key
    const row = findElinkRow(elinkId)
    if (!row || !key) return
    const source = rowEblocksSource(row)
    const target = source.find((eb) => (eb.key || '') === key || (eb.uuid || '') === (t.eblock?.uuid || ''))
    const equipmentId = target?.equipmentId ?? target?.id ?? target?.eblockId ?? null
    if (equipmentId != null) {
      try {
        await delEBlockV3(equipmentId)
        eui.showToast('已删除 eBlock', 'success')
        await loadElinkList()
      } catch (err) {
        console.error('删除 eBlock 失败:', err)
        eui.showToast(err?.message || '删除 eBlock 失败', 'error')
        return
      }
    } else {
      const next = eblocksForRow(row).filter((eb) => eb.key !== key)
      updateElinkEblocks(elinkId, next)
      eui.showToast('已删除 eBlock', 'success')
    }
  } else if (deleteDialog.kind === 'meter') {
    const id = t.id
    if (id != null) {
      try {
        await delMeterList(id)
        eui.showToast('已删除电表', 'success')
        await loadMeterList()
      } catch (err) {
        console.error('删除电表失败:', err)
        eui.showToast(err?.message || '删除电表失败', 'error')
        return
      }
    } else {
      meterRows.value = meterRows.value.filter((m) => m.id !== t.id)
      eui.showToast('已删除电表', 'success')
    }
  } else if (deleteDialog.kind === 'elink-water-machine') {
    const elinkId = t.elinkId
    const row = findElinkRow(elinkId)
    if (!row) return
    const waterMachineId = t.id
    if (waterMachineId != null) {
      try {
        await delWaterMachineList(waterMachineId)
        const latestWaterMachines = await loadWaterMachineList()
        refreshRowWaterMachinesFromList(row, latestWaterMachines)
        eui.showToast('已删除 eLink 水机', 'success')
      } catch (err) {
        console.error('删除 eLink 水机失败:', err)
        eui.showToast(err?.message || '删除 eLink 水机失败', 'error')
        return
      }
    } else {
      const nextList = elinkWaterMachinesForExpand(row)
        .filter((item) => item.key !== t.key && !(item.uuid === t.uuid && item.deviceId === t.deviceId))
        .map((item, index) => normalizeElinkWaterMachineItem(item, Math.max(0, Number(row.badge ?? row.id) || 0), index))
      syncRowWaterMachines(row, nextList)
      eui.showToast('已删除 eLink 水机', 'success')
    }
  } else if (deleteDialog.kind === 'elink-meter') {
    const id = t.id
    if (id != null) {
      try {
        await delMeterList(id)
        eui.showToast('已删除计量电表', 'success')
        await loadElinkList()
      } catch (err) {
        console.error('删除计量电表失败:', err)
        eui.showToast(err?.message || '删除计量电表失败', 'error')
        return
      }
    } else if (t.elinkId != null) {
      const row = findElinkRow(t.elinkId)
      if (row) {
        const next = meteringMetersForExpand(row).filter((meter) => meter.key !== t.key)
        updateElinkMeteringMeters(t.elinkId, next)
      }
      eui.showToast('已删除计量电表', 'success')
    }
  } else if (deleteDialog.kind === 'transformer') {
    const id = t.id
    if (id != null) {
      delTransformerList(id)
        .then(() => {
          eui.showToast('已删除变压器', 'success')
          loadTransformerList()
        })
        .catch((err) => console.error('删除变压器失败:', err))
    } else {
      transformerRows.value = transformerRows.value.filter((r) => r.id !== t.id)
      eui.showToast('已删除变压器', 'success')
    }
  } else if (deleteDialog.kind === 'pv') {
    const id = t.id
    if (id != null) {
      delPvList(id)
        .then(() => {
          eui.showToast('已删除光伏', 'success')
          loadPvList()
        })
        .catch((err) => console.error('删除光伏失败:', err))
    } else {
      pvRows.value = pvRows.value.filter((r) => r.id !== t.id)
      eui.showToast('已删除光伏', 'success')
    }
  } else if (deleteDialog.kind === 'pile') {
    const id = t.id
    if (id != null) {
      delChargePileList(id)
        .then(() => {
          eui.showToast('已删除充电桩', 'success')
          loadPileList()
        })
        .catch((err) => console.error('删除充电桩失败:', err))
    } else {
      pileRows.value = pileRows.value.filter((r) => r.id !== t.id)
      eui.showToast('已删除充电桩', 'success')
    }
  }
  closeDeleteDialog()
}

function toggleElink(id) {
  const cur = expandedElinkIds.value
  if (cur.includes(id)) {
    expandedElinkIds.value = cur.filter((x) => x !== id)
  } else {
    expandedElinkIds.value = [...cur, id]
  }
}

// 供父组件（建站向导）校验"储能系统必须有内容"，用于设备接入下一步硬校验
defineExpose({
  hasEnergyStorage: () => elinkRows.value.length > 0,
})

</script>

<style scoped lang="scss" src="./step-device/styles/step-device.scss"></style>
